// Guide articles ported from Intygslakaren reference package (2026-09-21).
// Swedish content copied verbatim from source; English produced from english-guides.ts
// templates and english-services.ts translations. Prices inlined from seafarer.ts,
// offshore.ts, work-study.ts, sport-certificates.ts, work-environment.ts,
// medical-driving.ts – no Next.js imports needed.

import { certificatePathFor, findCertificateBySlug } from "@/data/certificateSlugs";

export type GuideSection = {
  id: string;
  title: { sv: string; en: string };
  paragraphs: { sv: string[]; en: string[] };
  items?: { sv: string[]; en: string[] };
  table?: {
    columns: { sv: string[]; en: string[] };
    rows: { sv: string[][]; en: string[][] };
  };
  link?: { href: string; label: { sv: string; en: string } };
};

export type GuideArticle = {
  category: string;
  slug: string;
  label: { sv: string; en: string };
  title: { sv: string; en: string };
  description: { sv: string; en: string };
  sections: GuideSection[];
  source?: { sv: string; en: string };
  /** Internal cert slug for CTA */
  relatedCert?: string;
  /** Optional booking query string (full href) */
  bookingHref?: string;
};

// ─────────────────────────────────────────────────────────────────────────────
// INLINED CONSTANTS (no imports from Next.js project)
// ─────────────────────────────────────────────────────────────────────────────
const offshoreExamItems = {
  sv: ["Vikt", "Längd", "Midjemått", "BMI", "Blodtryck", "Urinsticka", "Läkarbesök"],
  en: ["Weight", "Height", "Waist measurement", "BMI", "Blood pressure", "Urine dipstick", "Medical appointment"],
};

const commonWorkEnvPrep = {
  sv: [
    "Giltig legitimation och arbetsgivarens beställning.",
    "Arbetsgivarens riskbedömning och en beskrivning av arbetsuppgifterna.",
    "Uppgifter om tidigare medicinska kontroller och eventuell sista giltighetsdag.",
    "Aktuell läkemedelslista och relevanta medicinska underlag till själva besöket.",
  ],
  en: [
    "Valid photo ID and the employer's work order.",
    "The employer's risk assessment and a description of the work tasks.",
    "Details of previous medical examinations and any expiry date.",
    "Current medication list and relevant medical records.",
  ],
};

const nationwideInv = {
  sv: "Vi kan ordna provtagning och röntgen i hela Sverige.",
  en: "We can arrange sampling and X-rays across Sweden.",
};

// ─────────────────────────────────────────────────────────────────────────────
// ALL GUIDE ARTICLES
// ─────────────────────────────────────────────────────────────────────────────
export const guideArticles: GuideArticle[] = [

  // ── SJÖFOLK ─────────────────────────────────────────────────────────────
  {
    category: "sjofolk", slug: "manila-stcw",
    label: { sv: "Manila/STCW", en: "Manila/STCW" },
    title: { sv: "Vad är ett Manila/STCW-intyg?", en: "What is a Manila/STCW medical certificate?" },
    description: {
      sv: "Förstå vad sjöfolksintyget gäller, vem som behöver det och hur befattning och fartområde påverkar undersökningen.",
      en: "What a Swedish seafarer medical covers, who needs it and why your role and trading area matter.",
    },
    sections: [
      {
        id: "intyget",
        title: { sv: "Ett läkarintyg för arbete till sjöss", en: "A medical certificate for work at sea" },
        paragraphs: {
          sv: [
            "STCW är de internationella reglerna om sjöpersonals utbildning, behörigheter och vakthållning. Manilaändringarna är en uppdatering av regelverket, som också omfattar medicinska krav.",
            "Hos oss får du ett svenskt sjöfolksintyg enligt STCW med Manilaändringarna, med text på svenska och engelska. Besöket sker på mottagningen i Göteborg.",
          ],
          en: [
            "STCW sets international standards for seafarer training, certification and watchkeeping. The Manila amendments updated the framework, including medical requirements.",
            "Our Swedish seafarer medical certificate follows STCW with the Manila amendments and has text in Swedish and English. The examination takes place in person in Gothenburg.",
          ],
        },
      },
      {
        id: "behov",
        title: { sv: "När behöver jag sjöfolksintyg?", en: "Who needs it?" },
        paragraphs: {
          sv: [
            "Besättningsmedlemmar i yrkesmässig sjöfart behöver ett giltigt läkarintyg. Det kan också behövas när du ansöker om en sjöbefälsbehörighet. Ange vad intyget ska användas till när du bokar.",
            "Ska du börja en utbildning? Ta fram utbildningens aktuella intygskrav och ta med dem till besöket.",
          ],
          en: [
            "Crew in commercial shipping need a valid medical certificate. It may also be required when applying for a maritime qualification or starting training. Bring the current requirements from your employer or school.",
            "Starting a course? Obtain the current medical requirements from the training provider and bring them to your appointment.",
          ],
        },
      },
      {
        id: "befattning",
        title: { sv: "Varför frågar läkaren om mitt arbete?", en: "Your role determines the assessment" },
        paragraphs: {
          sv: [
            "Uppgifterna om din tjänst styr vilka medicinska krav läkaren ska bedöma. Däck, maskin, radio och övriga arbetsuppgifter ombord kan ha olika krav på exempelvis syn och hörsel.",
            "Berätta vilken befattning du ska ha, vilket fartområde du ska arbeta i och om det är första gången du söker intyget. Ta gärna med arbetsgivarens underlag.",
          ],
          en: [
            "Deck, engine room, radio and other duties can have different vision and hearing requirements. The information about your role determines which medical criteria the doctor applies.",
            "Tell us your role, trading area and whether this is your first application. Bring the employer's requirements if available.",
          ],
        },
        link: {
          href: "/guider/sjofolk/infor-besoket",
          label: { sv: "Se förberedelserna inför besöket", en: "Prepare for your appointment" },
        },
      },
      {
        id: "anslutning",
        title: { sv: "Kan ni utfärda intyget?", en: "Issuing the certificate" },
        paragraphs: {
          sv: [
            "Ja. Vi är anslutna till Sjöläkarwebben, det system som används av läkare som utfärdar svenska sjöfolksintyg. Du får intyget efter besöket om inga extra åtgärder eller kompletteringar krävs.",
          ],
          en: [
            "Yes. We are connected to Sjöläkarwebben. You receive the certificate after the appointment if no additional examinations or supporting documents are needed.",
          ],
        },
        link: {
          href: "/guider/sjofolk/intyg-och-registrering",
          label: { sv: "Så får du intyget och så registreras det", en: "How you receive and register the certificate" },
        },
      },
    ],
    source: {
      sv: "Faktaunderlag: Transportstyrelsens information om sjömansläkare och Sjöläkarwebben. Kontrollerat 8 september 2026.",
      en: "Source: Swedish Transport Agency information on seafarer doctors and Sjöläkarwebben. Checked 8 September 2026.",
    },
    relatedCert: "sjofolksintyg",
    bookingHref: "/boka-tid?intyg=sjofolksintyg",
  },

  {
    category: "sjofolk", slug: "horseltest",
    label: { sv: "Hörseltest och pris", en: "Hearing test and price" },
    title: { sv: "Hörseltest för sjöfolksintyg", en: "Hearing tests for a seafarer medical" },
    description: {
      sv: "Hörseltestet kostar 395 kr extra. Här ser du totalpriset och vad som gäller vid första ansökan och förnyelse.",
      en: "When a hearing test is required and what it costs: an additional 395 SEK.",
    },
    sections: [
      {
        id: "pris",
        title: { sv: "Vad kostar hörseltestet?", en: "Price of a hearing test" },
        paragraphs: {
          sv: ["Hörseltest debiteras som ett tillägg på 395 kr när det behövs. Det ingår inte i grundpriset för läkarundersökning och sjöfolksintyg."],
          en: ["The hearing test is charged as an additional 395 SEK when needed. It is not included in the base price for the medical examination and certificate."],
        },
        table: {
          columns: { sv: ["Undersökning", "Pris"], en: ["Examination", "Price"] },
          rows: {
            sv: [
              ["Läkarundersökning och sjöfolksintyg", "1 100 kr"],
              ["Hörseltest, tillägg", "395 kr"],
              ["Totalt med hörseltest", "1 495 kr"],
            ],
            en: [
              ["Medical examination and certificate", "1 100 SEK"],
              ["Hearing test supplement", "395 SEK"],
              ["Total with hearing test", "1 495 SEK"],
            ],
          },
        },
      },
      {
        id: "forsta-ansokan",
        title: { sv: "Vad gäller för förstagångsansökare?", en: "First-time applications" },
        paragraphs: {
          sv: [
            "Tonaudiometri krävs när du för första gången ska tillträda en befattning inom däck, maskin eller radiotelefoni med GMDSS-ansvar. Meddela vid bokningen om det är din första ansökan, så att hörseltestet kan planeras.",
            "För övrig personal ombord gäller andra hörselkrav. Vilken kontroll du behöver avgörs utifrån din befattning och läkarens bedömning.",
          ],
          en: [
            "Pure-tone audiometry is required when first taking up deck, engine room or radio duties with GMDSS responsibility. Tell us when booking if this is your first application.",
            "Other crew roles have different hearing requirements. Which examination you need is determined by your role and the doctor's assessment.",
          ],
        },
      },
      {
        id: "undersokningen",
        title: { sv: "Hur kontrolleras hörseln?", en: "How hearing is assessed" },
        paragraphs: {
          sv: [
            "Vid tonaudiometri undersöks hörseln med toner på olika frekvenser. Läkaren bedömer resultatet mot kraven för din tjänst. Hörselbedömning ingår även vid förnyelse, men tonaudiometri behöver inte automatiskt göras vid varje besök.",
            "Ta med tidigare hörselresultat och ditt tidigare sjöfolksintyg om du har dem. Berätta om hörselbesvär och om du använder hörapparat.",
          ],
          en: [
            "Pure-tone audiometry checks hearing at different frequencies. The doctor assesses the result against your role's requirements. Hearing is also assessed at renewal, but pure-tone audiometry is not automatically needed at every visit.",
            "Bring previous hearing results and tell the doctor about hearing difficulties or hearing aids.",
          ],
        },
      },
      {
        id: "efter-testet",
        title: { sv: "När får jag mitt intyg?", en: "After the test" },
        paragraphs: {
          sv: ["Du får intyget efter besöket om inga extra åtgärder eller kompletteringar krävs. Om hörseln behöver undersökas vidare berättar läkaren vilket underlag som saknas innan bedömningen kan bli klar."],
          en: ["You receive the certificate after the appointment if no additional examinations are required. If further hearing investigation is needed, the doctor explains what information is missing before the assessment can be completed."],
        },
        link: {
          href: "/guider/sjofolk/intyg-och-registrering",
          label: { sv: "Läs om intyget efter besöket", en: "Receiving your certificate after the visit" },
        },
      },
    ],
    source: {
      sv: "Faktaunderlag: Intygsläkarens ordinarie prisinformation och TSFS 2011:117, med ändringar genom TSFS 2026:18. Kontrollerat 8 september 2026.",
      en: "Source: Intygsläkaren price information and TSFS 2011:117 with amendments through TSFS 2026:18. Checked 8 September 2026.",
    },
    relatedCert: "sjofolksintyg",
    bookingHref: "/boka-tid?intyg=sjofolksintyg",
  },

  {
    category: "sjofolk", slug: "infor-besoket",
    label: { sv: "Inför besöket", en: "Prepare your appointment" },
    title: { sv: "Så går besöket för sjöfolksintyg till", en: "Preparing for your seafarer medical appointment" },
    description: {
      sv: "Boka ett fysiskt besök i Göteborg. Här ser du vad du ska ta med och vad som händer under läkarundersökningen.",
      en: "Book an in-person appointment in Gothenburg. What to bring and what happens during the medical examination.",
    },
    sections: [
      {
        id: "boka",
        title: { sv: "Kan jag göra undersökningen på video?", en: "Does it have to be in person?" },
        paragraphs: {
          sv: [
            "Sjöfolksintyget görs vid ett fysiskt besök på vår mottagning på Järntorgsgatan 8 i Göteborg. Tryck på Boka tid och välj Sjöfolksintyg och en ledig tid i kalendern.",
            "Läkarundersökning och intyg kostar 1 100 kr. För förstagångsansökare som behöver hörseltest tillkommer 395 kr, totalt 1 495 kr.",
          ],
          en: [
            "The seafarer medical requires an in-person visit at our clinic at Järntorgsgatan 8 in Gothenburg. Press Book appointment, select Seafarer medical and choose an available time.",
            "The medical examination and certificate cost 1 100 SEK. For first-time applicants who need a hearing test, an additional 395 SEK applies, totalling 1 495 SEK.",
          ],
        },
      },
      {
        id: "ta-med",
        title: { sv: "Vad ska jag ta med?", en: "What to bring" },
        paragraphs: {
          sv: ["Förbered följande så att läkaren kan bedöma rätt intyg och rätt tjänstgöring:"],
          en: ["Prepare the following so the doctor can assess the correct certificate and role:"],
        },
        items: {
          sv: [
            "Giltig legitimation.",
            "Tidigare sjöfolksintyg och hörselresultat, om du har dem.",
            "Glasögon eller kontaktlinser som du använder.",
            "Aktuell läkemedelslista och relevanta medicinska underlag.",
            "Din befattning, ditt fartområde och eventuella krav från arbetsgivaren eller utbildningen.",
          ],
          en: [
            "Valid photo ID.",
            "Previous seafarer medical and hearing results, if available.",
            "Glasses or contact lenses you use.",
            "Current medication list and relevant medical records.",
            "Your role, trading area and any requirements from your employer or training provider.",
          ],
        },
      },
      {
        id: "lakarundersokning",
        title: { sv: "Vad händer under undersökningen?", en: "During the examination" },
        paragraphs: {
          sv: [
            "Vid en fullständig undersökning går läkaren igenom din hälsodeklaration och gör en allmän undersökning samt bedömer syn och hörsel. Berätta om sjukdomar, behandlingar och besvär som kan påverka arbetet ombord.",
            "Har du medicinska underlag från en annan mottagning tar du med dem. Läkaren avgör om något behöver kompletteras för att kunna bedöma din tjänstbarhet.",
          ],
          en: [
            "For a full examination, the doctor reviews your health declaration, performs a general examination and assesses vision and hearing. Mention any illnesses, treatments or symptoms that may affect work on board.",
            "Bring medical records from another clinic if you have them. The doctor decides whether anything needs to be supplemented to assess your fitness for service.",
          ],
        },
        link: {
          href: "/guider/sjofolk/horseltest",
          label: { sv: "Läs om hörseltest vid första ansökan", en: "Hearing tests for first-time applicants" },
        },
      },
      {
        id: "klart",
        title: { sv: "Är intyget klart efter besöket?", en: "When is the certificate ready?" },
        paragraphs: {
          sv: ["Ja, om inga extra åtgärder eller kompletteringar krävs får du intyget efter besöket. Behövs till exempel ytterligare undersökning eller medicinska underlag får du veta vad nästa steg är."],
          en: ["Yes, if no additional measures or supplementary documents are required you receive the certificate after the appointment. If further examination or medical records are needed, the doctor explains the next step."],
        },
        link: {
          href: "/guider/sjofolk/intyg-och-registrering",
          label: { sv: "Läs om utlämning och registrering", en: "Receiving and registering your certificate" },
        },
      },
    ],
    source: {
      sv: "Faktaunderlag: Intygsläkarens besöksinformation och Transportstyrelsens föreskrifter om läkarintyg för sjöfolk. Kontrollerat 8 september 2026.",
      en: "Source: Intygsläkaren visit information and Swedish Transport Agency regulations on seafarer medical certificates. Checked 8 September 2026.",
    },
    relatedCert: "sjofolksintyg",
    bookingHref: "/boka-tid?intyg=sjofolksintyg",
  },

  {
    category: "sjofolk", slug: "intyg-och-registrering",
    label: { sv: "Intyg och registrering", en: "Certificate and registration" },
    title: { sv: "Ditt sjöfolksintyg efter besöket", en: "Your seafarer medical certificate after the visit" },
    description: {
      sv: "Så får du ditt intyg, hur Sjöläkarwebben och sjömansregistret hänger ihop och vad du ska ta med ombord.",
      en: "Receiving your certificate, registration in Sjöläkarwebben and the document to take on board.",
    },
    sections: [
      {
        id: "utlamning",
        title: { sv: "När får jag mitt intyg?", en: "When is the certificate ready?" },
        paragraphs: {
          sv: ["Du får intyget efter besöket om inga extra åtgärder eller kompletteringar krävs. Om läkaren behöver mer underlag får du besked om vad som återstår innan intyget kan färdigställas."],
          en: ["You receive it after the visit if no additional examinations or information are required. Otherwise the doctor explains what remains."],
        },
      },
      {
        id: "registrering",
        title: { sv: "Är ni anslutna och registreras intyget?", en: "Registration" },
        paragraphs: {
          sv: [
            "Ja. Vi är anslutna till Sjöläkarwebben. Våra läkare utfärdar intyget i detta system och intyget registreras i sjömansregistret.",
            "En kopia skickas automatiskt till Transportstyrelsen. Du behöver därför inte skicka in samma läkarintyg separat. När registreringen är klar skickas en bekräftelse via mejl.",
          ],
          en: [
            "We issue certificates in Sjöläkarwebben, and the certificate is registered in the seafarer register. A copy is sent automatically to the Swedish Transport Agency, so you do not need to send the same certificate separately.",
            "When registration is complete, a confirmation is sent by email.",
          ],
        },
      },
      {
        id: "ombord",
        title: { sv: "Vad ska jag ha med mig ombord?", en: "Take the original on board" },
        paragraphs: {
          sv: ["Ta med det giltiga originalintyget, undertecknat av läkaren. En digital registrering ersätter inte originalet ombord. Intyget visar bland annat bedömningen av din hälsa, syn och hörsel samt eventuella begränsningar."],
          en: ["Take your valid original certificate signed by the doctor. Digital registration does not replace the original on board. The certificate records the assessment of your health, vision and hearing and any restrictions."],
        },
        link: {
          href: "/guider/sjofolk/giltighetstid",
          label: { sv: "Kontrollera intygets giltighetstid", en: "Check the certificate's validity period" },
        },
      },
      {
        id: "forlorat",
        title: { sv: "Vad gör jag om intyget har försvunnit?", en: "If the certificate is lost" },
        paragraphs: {
          sv: ["Kontakta läkaren som utfärdade intyget för att få en undertecknad dubblett. Om vi har utfärdat ditt intyg hjälper vi dig med nästa steg."],
          en: ["Contact the issuing doctor for a signed duplicate. If we issued your certificate, contact us for help."],
        },
        link: {
          href: "/kontakt",
          label: { sv: "Kontakta mottagningen", en: "Contact the clinic" },
        },
      },
    ],
    source: {
      sv: "Faktaunderlag: Transportstyrelsens information om läkarintyg för sjöfolk och Sjöläkarwebben. Kontrollerat 8 september 2026.",
      en: "Source: Swedish Transport Agency information on seafarer medical certificates and Sjöläkarwebben. Checked 8 September 2026.",
    },
    relatedCert: "sjofolksintyg",
    bookingHref: "/boka-tid?intyg=sjofolksintyg",
  },

  {
    category: "sjofolk", slug: "giltighetstid",
    label: { sv: "Giltighetstid", en: "Validity period" },
    title: { sv: "Hur länge gäller sjöfolksintyget?", en: "How long is a seafarer medical certificate valid?" },
    description: {
      sv: "Se giltighetstiderna för tjänstgöring och vad som gäller när intyget används vid en behörighetsansökan.",
      en: "Maximum validity periods for service at sea and the separate requirement for qualification applications.",
    },
    sections: [
      {
        id: "tjanstgoring",
        title: { sv: "Giltighet vid tjänstgöring", en: "Validity for service at sea" },
        paragraphs: {
          sv: ["Giltigheten beror på din ålder och vilken fart intyget gäller. Tabellen visar de längsta ordinarie tiderna. Läkaren kan ange en kortare tid."],
          en: ["The maximum ordinary validity depends on age and trading area. The doctor may specify a shorter period."],
        },
        table: {
          columns: { sv: ["Situation", "Längsta giltighet"], en: ["Situation", "Maximum validity"] },
          rows: {
            sv: [
              ["Du är under 18 år", "1 år"],
              ["Du är 18 år eller äldre – begränsad eller obegränsad fart", "2 år"],
              ["Du är 18 år eller äldre – inre fart eller fiskare", "4 år"],
            ],
            en: [
              ["Under 18", "1 year"],
              ["Age 18 or over, limited or unlimited trading area", "2 years"],
              ["Age 18 or over, internal waters or fishing", "4 years"],
            ],
          },
        },
      },
      {
        id: "behorighet",
        title: { sv: "Gäller samma tid vid en behörighetsansökan?", en: "Qualification applications" },
        paragraphs: {
          sv: ["Vid behörighetsansökan får intyget vara högst två år gammalt, även om det gäller i fyra år för tjänstgöring. För behörighet i inre fart ska intyget visa att aktuella syn- och hörselkrav är uppfyllda."],
          en: ["For a qualification application, the certificate must be no more than two years old, even if it is valid for four years for service. For internal waters qualifications it must demonstrate that the current vision and hearing requirements are met."],
        },
      },
      {
        id: "fornya",
        title: { sv: "När ska jag boka förnyelse?", en: "Plan your renewal" },
        paragraphs: {
          sv: [
            "Utgå från sista giltighetsdagen på ditt eget intyg och planera besöket före nästa tjänstgöring. Ta med det tidigare intyget och berätta om din befattning eller ditt fartområde har ändrats.",
            "Boka med utrymme för eventuella kompletteringar. Du får det nya intyget efter besöket om inga extra åtgärder krävs.",
          ],
          en: [
            "Check the expiry date on your own certificate and book before your next period of service. Bring the previous certificate and tell us if your role or trading area has changed.",
            "Leave time for any additional examinations. You receive the new certificate after the appointment if no extra steps are needed.",
          ],
        },
        link: {
          href: "/guider/sjofolk/infor-besoket",
          label: { sv: "Förbered dig inför förnyelsen", en: "Prepare for your renewal" },
        },
      },
    ],
    source: {
      sv: "Faktaunderlag: Transportstyrelsens information om giltighet vid tjänstgöring och behörighetsansökan. Kontrollerat 8 september 2026.",
      en: "Source: Swedish Transport Agency information on validity for service and qualification applications. Checked 8 September 2026.",
    },
    relatedCert: "sjofolksintyg",
    bookingHref: "/boka-tid?intyg=sjofolksintyg",
  },

  // ── HÖGRE BEHÖRIGHET ──────────────────────────────────────────────────────
  {
    category: "hogre-behorighet", slug: "synintyg",
    label: { sv: "Syntest och synintyg", en: "Vision tests" },
    title: { sv: "Syntest för buss- och lastbilsintyg", en: "Vision tests for truck and bus medical certificates" },
    description: {
      sv: "Så fungerar syntest på mottagningen och separata synuppgifter när du väljer läkarbesök via video.",
      en: "Vision tests at the clinic and the separate vision and blood pressure information needed for video appointments.",
    },
    sections: [
      {
        id: "besoksform",
        title: { sv: "Ingår syntestet?", en: "Is the vision test included?" },
        paragraphs: { sv: [], en: [] },
        table: {
          columns: { sv: ["Besöksform", "Pris och syntest"], en: ["Visit type", "Price and vision test"] },
          rows: {
            sv: [
              ["Fysiskt besök i Göteborg", "1 100 kr – syntest ingår"],
              ["Videobesök", "995 kr – syntest och blodtryckskontroll ordnas och betalas separat"],
            ],
            en: [
              ["Gothenburg clinic", "1 100 SEK, vision test included"],
              ["Video consultation", "995 SEK; vision and blood pressure checks arranged and paid for separately"],
            ],
          },
        },
      },
      {
        id: "optiker",
        title: { sv: "Hur gör jag syntestet inför video?", en: "Before a video appointment" },
        paragraphs: {
          sv: [
            "Boka ett syntest hos optiker och berätta att det gäller högre körkortsbehörighet. Ange vilka behörigheter du söker. Följ våra instruktioner inför besöket om hur synuppgifterna ska lämnas.",
            "När en optiker lämnar synuppgifterna separat anger läkaren detta i läkarintyget. Du behöver se till att båda underlagen kommer in i ditt ärende.",
          ],
          en: [
            "Book a vision test with an optician and explain that it is for higher Swedish driving licence categories. Tell them the categories you are applying for. Follow our appointment instructions on submitting the information.",
            "The doctor records when an optician supplies the vision information separately. Make sure that both parts of the information reach your case.",
          ],
        },
      },
      {
        id: "utokat",
        title: { sv: "När behövs ett utökat synintyg?", en: "When more vision information is needed" },
        paragraphs: {
          sv: ["Om det vanliga synunderlaget inte räcker för att bedöma synkraven kan ett utökat synintyg behövas. Det utfärdas av optiker eller läkare med specialistkompetens inom ögonsjukdomar enligt kraven i ärendet. Ta med eventuella brev eller förelägganden till besöket."],
          en: ["An extended vision certificate may be needed if the ordinary information is insufficient. This is provided by an optician or an ophthalmologist according to the case requirements. Bring any decision or request letters."],
        },
      },
      {
        id: "forbered",
        title: { sv: "Vad mer ska jag förbereda?", en: "Other preparations" },
        paragraphs: {
          sv: ["Ha legitimation, läkemedelslista och glasögon eller kontaktlinser tillgängliga. Vid videobesök behöver du också ordna en aktuell blodtryckskontroll enligt instruktionerna inför ditt besök. Den kostnaden ingår inte i priset 995 kr."],
          en: ["Have photo ID, medication details and glasses or contact lenses ready. Arrange a current blood pressure check according to the instructions. Its cost is not included in the 995 SEK video consultation."],
        },
        link: {
          href: "/guider/hogre-behorighet/horselkrav",
          label: { sv: "Läs också om hörselkraven för buss", en: "Hearing requirements for bus licences" },
        },
      },
    ],
    source: {
      sv: "Faktaunderlag: Transportstyrelsens information om medicinska intyg och separata synuppgifter. Kontrollerat 8 september 2026.",
      en: "Source: Swedish Transport Agency information on medical certificates and separate vision information. Checked 8 September 2026.",
    },
    relatedCert: "hogre-behorighet",
    bookingHref: "/boka-tid?intyg=hogre-behorighet",
  },

  {
    category: "hogre-behorighet", slug: "horselkrav",
    label: { sv: "Hörselkrav för buss", en: "Hearing requirements for bus" },
    title: { sv: "Vilka hörselkrav gäller för buss och lastbil?", en: "Hearing requirements for bus and truck licences" },
    description: {
      sv: "Läs om skillnaden mellan C- och D-behörigheter, hur hörseln prövas och vad det betyder för ditt besök.",
      en: "How hearing requirements differ between truck and bus categories and how the hearing assessment is arranged.",
    },
    sections: [
      {
        id: "skillnad",
        title: { sv: "Kraven skiljer sig mellan buss och lastbil", en: "Different licence categories" },
        paragraphs: { sv: [], en: [] },
        table: {
          columns: { sv: ["Behörighet", "Hörselkrav"], en: ["Licence", "Hearing requirement"] },
          rows: {
            sv: [
              ["Lastbil: C1, C1E, C och CE", "Hörselnedsättning eller dövhet utgör i sig inget hinder."],
              ["Buss: D1, D1E, D och DE", "Du ska kunna uppfatta vanlig samtalsröst på fyra meters avstånd med minst ett öra. Hörapparat får användas."],
            ],
            en: [
              ["Truck: C1, C1E, C and CE", "Hearing impairment or deafness is not, by itself, a barrier."],
              ["Bus: D1, D1E, D and DE", "Ordinary conversation must be heard at four metres with at least one ear. A hearing aid may be used."],
            ],
          },
        },
      },
      {
        id: "provning",
        title: { sv: "Hur prövas hörseln?", en: "The hearing assessment" },
        paragraphs: {
          sv: ["För bussbehörighet görs prövningen med samtalsröst eller tonaudiometer. Berätta om hörselbesvär och ta med hörapparat om du använder en. Ange alla behörigheter när intyget ska omfatta både buss och lastbil."],
          en: ["For bus categories, hearing is assessed using conversational speech or pure-tone audiometry. Tell the doctor about hearing difficulties and bring your hearing aid if you use one."],
        },
      },
      {
        id: "video",
        title: { sv: "Vad innebär det om jag väljer video?", en: "If you choose video" },
        paragraphs: {
          sv: [
            "Hörselkravet för buss behöver bedömas även om läkarkontakten sker via video. Vi går igenom hur de nödvändiga undersökningsmomenten ska genomföras och om ett besök på plats behövs.",
            "Videobesök kostar 995 kr och kräver separat blodtryckskontroll och syntest, som inte ingår i priset. Ett fysiskt besök i Göteborg kostar 1 100 kr och då ingår syntest.",
          ],
          en: [
            "The hearing requirement still needs to be assessed. We explain how the necessary examination is arranged and whether you need an in-person visit.",
            "Video costs 995 SEK, with vision and blood pressure checks arranged and paid for separately; a clinic appointment costs 1 100 SEK including the vision test.",
          ],
        },
        link: {
          href: "/guider/hogre-behorighet/synintyg",
          label: { sv: "Läs om syntest och separata synuppgifter", en: "Vision tests and separate vision information" },
        },
      },
      {
        id: "bada",
        title: { sv: "Kan jag boka för både buss och lastbil?", en: "Both bus and truck" },
        paragraphs: {
          sv: ["Ja. Välj intyget för både buss och lastbil och ange samtliga behörigheter inför besöket. Efter Boka tid väljer du video eller fysiskt besök och därefter en ledig tid."],
          en: ["Choose the certificate for both bus and truck and state all the categories you need. Then choose between video and in-person, and select an available time."],
        },
        link: {
          href: "/lakarintyg/buss-lastbil",
          label: { sv: "Se intyget för både buss och lastbil", en: "Bus and truck medical certificate" },
        },
      },
    ],
    source: {
      sv: "Faktaunderlag: Transportstyrelsens medicinska krav om hörsel och balanssinne. Kontrollerat 8 september 2026.",
      en: "Source: Swedish Transport Agency medical requirements for hearing and balance. Checked 8 September 2026.",
    },
    relatedCert: "buss-lastbil",
    bookingHref: "/boka-tid?intyg=hogre-behorighet",
  },

  // ── OFFSHORE NORGE ────────────────────────────────────────────────────────
  {
    category: "offshore-norge", slug: "krav-och-intyg",
    label: { sv: "Norska krav och intyg", en: "Certificate requirements" },
    title: { sv: "Vad krävs för offshoreintyg i Norge?", en: "What is required for a Norwegian offshore medical?" },
    description: {
      sv: "Läs om norsk helseerklæring, godkänd petroleumslege och vad som händer efter den medicinska bedömningen.",
      en: "About the Norwegian health declaration, approved petroleum doctor and what happens after the assessment.",
    },
    sections: [
      {
        id: "nar",
        title: { sv: "När behöver jag ett norskt offshoreintyg?", en: "When do I need a Norwegian offshore medical?" },
        paragraphs: {
          sv: [
            "För arbete på fasta eller flyttbara installationer inom norsk petroleumverksamhet behövs en medicinsk hälsoförklaring, på norska helseerklæring. Den gäller din lämplighet för arbete offshore.",
            "Ska du arbeta ombord på ett fartyg kan ett sjöfolksintyg vara aktuellt i stället. Ta fram arbetsgivarens intygskrav så hjälper vi dig att välja rätt undersökning.",
          ],
          en: [
            "For work on fixed or mobile installations in Norwegian petroleum operations, a medical health declaration (helseerklæring) is required. It covers your fitness for offshore work.",
            "If you work on board a vessel, a seafarer medical may be appropriate instead. Obtain your employer's certificate requirements and we will help you choose the right examination.",
          ],
        },
        link: {
          href: "/lakarintyg/sjofolksintyg",
          label: { sv: "Läs om sjöfolksintyg för arbete ombord", en: "Seafarer medical for work on board" },
        },
      },
      {
        id: "godkannande",
        title: { sv: "Vad innebär godkänd petroleumslege?", en: "Approved petroleum doctor" },
        paragraphs: {
          sv: [
            "Petroleumslege är en läkare som är godkänd att utfärda norska offshoreintyg. Statsforvaltaren i Rogaland ansvarar för området.",
            "Vi är registrerade hos Statsforvaltaren i Rogaland och erbjuder den norska offshoreundersökningen på vår mottagning i Göteborg.",
          ],
          en: [
            "A petroleumslege is a doctor approved to issue Norwegian offshore health declarations. Statsforvaltaren in Rogaland oversees this area.",
            "We are registered with Statsforvaltaren in Rogaland and offer the Norwegian offshore examination at our Gothenburg clinic.",
          ],
        },
      },
      {
        id: "bedomning",
        title: { sv: "Vad bedömer läkaren?", en: "What does the doctor assess?" },
        paragraphs: {
          sv: [
            "Läkaren bedömer om din hälsa fungerar med arbetsuppgifterna och säkerheten offshore. Berätta om sjukdomar, behandlingar och de krav som gäller för din tjänst.",
            "Om hälsokraven inte är uppfyllda kan mer utredning eller dispensationsprövning behövas. Petroleumslegen går då igenom nästa steg med dig.",
          ],
          en: [
            "The doctor assesses whether your health is compatible with your duties and safety requirements offshore. Mention any illnesses, treatments and the requirements for your role.",
            "If the health requirements are not met, further investigation or exemption assessment may be needed. The petroleum doctor will explain the next steps.",
          ],
        },
      },
      {
        id: "intyget",
        title: { sv: "När får jag intyget och hur ser jag giltigheten?", en: "When is the certificate ready?" },
        paragraphs: {
          sv: [
            "Intyget färdigställs när den medicinska bedömningen och nödvändiga underlag är klara. Sista giltighetsdag står på intyget. Du behåller originalet och en kopia finns i patientjournalen.",
            "Ta med ditt tidigare intyg vid förnyelse och planera besöket före nästa utresa. Ange gärna önskad period i kontaktformuläret.",
          ],
          en: [
            "The certificate is completed when the medical assessment and necessary records are ready. The expiry date is shown on the certificate. You keep the original; a copy is retained in the patient record.",
            "Bring your previous certificate at renewal and plan the visit before your next departure. Include the desired period in the contact form.",
          ],
        },
        link: {
          href: "/lakarintyg/offshore#kontaktformular",
          label: { sv: "Kontakta oss för att planera besöket", en: "Contact us to plan your visit" },
        },
      },
    ],
    source: {
      sv: "Faktaunderlag: Statsforvaltaren i Rogalands information och hälsointyg för offshore. Kontrollerat 8 september 2026.",
      en: "Source: Statsforvaltaren in Rogaland information on offshore health declarations. Checked 8 September 2026.",
    },
    relatedCert: "offshore",
  },

  {
    category: "offshore-norge", slug: "infor-besoket",
    label: { sv: "Inför besöket i Göteborg", en: "Prepare your appointment" },
    title: { sv: "Så förbereder du ditt besök för Offshore Norge", en: "Preparing your Norwegian offshore medical in Gothenburg" },
    description: {
      sv: "Undersökningen kostar 2 500 kr och görs fysiskt i Göteborg. Se vad som ingår och hur vi planerar din tid.",
      en: "The examination costs 2 500 SEK and is carried out in person in Gothenburg. What is included and how we plan your visit.",
    },
    sections: [
      {
        id: "planera",
        title: { sv: "Hur bokar jag min undersökning?", en: "How to book" },
        paragraphs: {
          sv: [
            "Skicka kontaktformuläret på sidan för Offshore Norge. Ange namn och telefonnummer samt gärna e-postadress och när du önskar komma.",
            "Vi kontaktar dig för att gå igenom ditt intygsbehov och komma överens om en tid. Du får besked om förberedelserna innan besöket. En förfrågan blir en bokning först när vi har bekräftat tiden tillsammans.",
          ],
          en: [
            "Send the contact form on the Offshore Norway page. Include your name, phone number and, if possible, email address and preferred dates.",
            "We will contact you to review your certificate requirements and agree on a time. You will receive preparation instructions before the visit. An enquiry becomes a booking only once we have confirmed the time together.",
          ],
        },
        link: {
          href: "/lakarintyg/offshore#kontaktformular",
          label: { sv: "Till kontaktformuläret för Norge", en: "Contact form for Norwegian offshore" },
        },
      },
      {
        id: "pris",
        title: { sv: "Vad ingår för 2 500 kr?", en: "What is included for 2 500 SEK?" },
        paragraphs: {
          sv: ["I priset för Offshore Norge ingår följande moment:"],
          en: ["The Norwegian offshore examination package includes:"],
        },
        items: offshoreExamItems,
      },
      {
        id: "forberedelser",
        title: { sv: "Vad behöver jag ta med?", en: "What to bring" },
        paragraphs: {
          sv: ["Förbered underlaget nedan. Hälsouppgifter lämnar du i journalsystemet eller till vårdpersonalen, inte i kontaktformuläret."],
          en: ["Prepare the items below. Health information is submitted via the medical record system or to the care team – not in the contact form."],
        },
        items: {
          sv: [
            "Giltig legitimation och eventuellt tidigare offshoreintyg.",
            "Glasögon eller kontaktlinser om du använder det.",
            "Aktuell läkemedelslista och relevanta medicinska underlag.",
            "Arbetsgivarens intygskrav, arbetsuppgifter och planerad utresa.",
          ],
          en: [
            "Valid photo ID and any previous offshore medical.",
            "Glasses or contact lenses if you use them.",
            "Current medication list and relevant medical records.",
            "Employer's certificate requirements, job description and planned departure.",
          ],
        },
      },
      {
        id: "mottagningen",
        title: { sv: "Var sker undersökningen?", en: "Where does the examination take place?" },
        paragraphs: {
          sv: [
            "Besöket görs enbart fysiskt på Järntorgsgatan 8 i Göteborg. Där genomförs mätningarna, urinstickan och läkarbesöket.",
            "Läkaren går igenom din hälsa och tjänstgöring. Om bedömningen kräver fler uppgifter får du besked om vad som behöver kompletteras.",
          ],
          en: [
            "The examination takes place exclusively in person at Järntorgsgatan 8 in Gothenburg. Measurements, urine dipstick and the medical appointment are all carried out there.",
            "The doctor reviews your health and work situation. If the assessment requires more information, you will be told what needs to be supplemented.",
          ],
        },
      },
    ],
    source: {
      sv: "Pris, innehåll och besöksupplägg enligt Intygsläkaren. Informationen om norskt offshoreintyg kontrollerad 8 september 2026.",
      en: "Source: Intygsläkaren price, content and visit information. Norwegian offshore information checked 8 September 2026.",
    },
    relatedCert: "offshore",
  },

  // ── OEUK ──────────────────────────────────────────────────────────────────
  {
    category: "oeuk", slug: "krav-och-intyg",
    label: { sv: "OEUK och godkännande", en: "Certificate requirements" },
    title: { sv: "Vad är ett OEUK offshore medical?", en: "What is an OEUK offshore medical?" },
    description: {
      sv: "Förstå vad OEUK-intyget gäller, hur den medicinska bedömningen fungerar och vad vårt godkännande innebär.",
      en: "What an OEUK certificate covers, how the assessment works and what our approval means.",
    },
    sections: [
      {
        id: "oeuk",
        title: { sv: "Ett medicinskt intyg för offshorearbete", en: "A medical certificate for offshore work" },
        paragraphs: {
          sv: [
            "OEUK står för Offshore Energies UK. OEUK-intyget används för att bedöma medicinsk lämplighet för offshorearbete, framför allt inom den brittiska offshoresektorn. Namnet OGUK används fortfarande ibland när arbetsgivare efterfrågar detta intyg.",
            "Arbetsmiljön och arbetsuppgifterna är en del av den medicinska bedömningen. Ange därför vilken tjänst, arbetsplats och typ av intyg arbetsgivaren efterfrågar.",
          ],
          en: [
            "OEUK stands for Offshore Energies UK. The OEUK medical is used to assess fitness for offshore work, primarily in the UK offshore sector. The name OGUK is still sometimes used by employers.",
            "The work environment and duties are part of the medical assessment. State the role, workplace and type of certificate your employer requires.",
          ],
        },
      },
      {
        id: "godkanda",
        title: { sv: "Är ni godkända enligt OEUK?", en: "Are you approved under OEUK?" },
        paragraphs: {
          sv: ["Ja. Vi är godkända enligt OEUK och erbjuder undersökningen på vår mottagning i Göteborg. Bedömningen görs enligt OEUK:s medicinska riktlinjer."],
          en: ["Yes. We are approved under OEUK and offer the examination at our Gothenburg clinic. The assessment follows OEUK medical guidelines."],
        },
      },
      {
        id: "underlag",
        title: { sv: "Vad händer om mer underlag behövs?", en: "If more information is needed" },
        paragraphs: {
          sv: [
            "Läkaren går igenom din hälsa och avgör om underlaget räcker för bedömningen. Behövs till exempel ett kompletterande medicinskt utlåtande får du besked om vad som återstår.",
            "Intyget färdigställs när den medicinska bedömningen är klar. Följ giltighetsdatum och eventuella villkor på ditt eget intyg när du planerar arbete och förnyelse.",
          ],
          en: [
            "The doctor reviews your health and decides whether the information is sufficient. If a supplementary medical report is needed, you will be told what remains.",
            "The certificate is completed when the medical assessment is finished. Follow the expiry date and any conditions on your certificate when planning work and renewal.",
          ],
        },
      },
      {
        id: "utbildning",
        title: { sv: "Behöver jag också intyg inför säkerhetsutbildning?", en: "Certificates for safety training" },
        paragraphs: {
          sv: [
            "En utbildningsanordnare kan efterfråga en särskild medicinsk bedömning inför offshoreutbildning. Berätta när vi planerar besöket om du också ska genomgå säkerhetsutbildning och ta fram utbildningens krav.",
            "Vi går igenom vilket underlag du behöver, så att bokningen avser rätt undersökning.",
          ],
          en: [
            "A training provider may require a specific medical assessment before offshore safety training. Tell us when planning your visit if you are also attending safety training, and bring the training requirements.",
            "We review what documentation you need to ensure the booking covers the right examination.",
          ],
        },
        link: {
          href: "/lakarintyg/oguk-ouek#kontaktformular",
          label: { sv: "Kontakta oss om ditt OEUK-besök", en: "Contact us about your OEUK appointment" },
        },
      },
    ],
    source: {
      sv: "Faktaunderlag: OEUK:s information om läkare, medicinska riktlinjer och frågor för offshorepersonal. Kontrollerat 8 september 2026.",
      en: "Source: OEUK information on doctors, medical guidelines and offshore personnel FAQs. Checked 8 September 2026.",
    },
    relatedCert: "oguk",
  },

  {
    category: "oeuk", slug: "infor-besoket",
    label: { sv: "Pris och förberedelser", en: "Price and preparations" },
    title: { sv: "Inför din OEUK-undersökning i Göteborg", en: "Preparing your OEUK examination in Gothenburg" },
    description: {
      sv: "OEUK kostar 5 000 kr. Se undersökningens innehåll, vad du ska ta med och hur du får en tid.",
      en: "The OEUK examination costs 5 000 SEK. What is included, what to bring and how to arrange your visit.",
    },
    sections: [
      {
        id: "pris",
        title: { sv: "Vad ingår för 5 000 kr?", en: "What is included for 5 000 SEK?" },
        paragraphs: {
          sv: ["OEUK-undersökningen genomförs på mottagningen. Följande moment ingår i priset:"],
          en: ["The OEUK examination is carried out at the clinic. The following are included:"],
        },
        items: offshoreExamItems,
      },
      {
        id: "kontakt",
        title: { sv: "Hur planerar vi besöket?", en: "How to plan your visit" },
        paragraphs: {
          sv: [
            "Börja med kontaktformuläret på OEUK-sidan. Lämna namn och telefonnummer samt gärna e-postadress och önskad period för undersökningen.",
            "Vi återkommer för att stämma av ditt intygsbehov och komma överens om en tid. Det är ett fysiskt besök på Järntorgsgatan 8 i Göteborg. Du väljer ingen videotid.",
          ],
          en: [
            "Start with the contact form on the OEUK page. Include your name, phone number and, if possible, email address and preferred dates.",
            "We will contact you to review your certificate requirements and agree on a time. This is an in-person visit at Järntorgsgatan 8 in Gothenburg – no video option.",
          ],
        },
        link: {
          href: "/lakarintyg/oguk-ouek#kontaktformular",
          label: { sv: "Till kontaktformuläret för OEUK", en: "Contact form for OEUK" },
        },
      },
      {
        id: "ta-med",
        title: { sv: "Vad ska jag ha med mig?", en: "What to bring" },
        paragraphs: {
          sv: ["Ta fram det här inför besöket:"],
          en: ["Prepare the following before your visit:"],
        },
        items: {
          sv: [
            "Giltig legitimation och eventuellt tidigare offshoreintyg.",
            "Glasögon eller kontaktlinser om du använder det.",
            "Aktuell läkemedelslista och relevanta medicinska underlag.",
            "Arbetsgivarens intygskrav, arbetsuppgifter och planerad utresa.",
          ],
          en: [
            "Valid photo ID and any previous offshore medical.",
            "Glasses or contact lenses if you use them.",
            "Current medication list and relevant medical records.",
            "Employer's certificate requirements, job description and planned departure.",
          ],
        },
      },
      {
        id: "arbetsgivaren",
        title: { sv: "Vad behöver ni veta om mitt arbete?", en: "Information about your work" },
        paragraphs: {
          sv: [
            "Berätta vilket land eller vilken offshoreanläggning du ska arbeta i, vilka arbetsuppgifter du ska ha och när du planerar att resa ut. Om du har särskilda beredskapsuppgifter eller ska gå en säkerhetsutbildning tar du också med dessa krav.",
            "Medicinska uppgifter går vi igenom via journalsystemet och vid läkarbesöket. Kontaktformuläret är till för kontaktuppgifter och tidsplanering.",
          ],
          en: [
            "Tell us the country or installation, your duties and when you plan to depart. If you have emergency response duties or are attending safety training, bring those requirements too.",
            "Medical information is handled via the medical record system and at the appointment. The contact form is for contact details and scheduling.",
          ],
        },
      },
    ],
    source: {
      sv: "Pris, innehåll och besöksupplägg enligt Intygsläkaren. Faktaunderlag om OEUK kontrollerat 8 september 2026.",
      en: "Source: Intygsläkaren price, content and visit information. OEUK information checked 8 September 2026.",
    },
    relatedCert: "oguk",
  },

  {
    category: "oeuk", slug: "norge-eller-oeuk",
    label: { sv: "Välj rätt offshoreintyg", en: "Choose the right offshore medical" },
    title: { sv: "Offshore Norge eller OEUK – vilket intyg behöver jag?", en: "Norwegian offshore or OEUK: which medical do you need?" },
    description: {
      sv: "Jämför de två intygen och priserna. Arbetsgivarens krav hjälper dig att välja rätt inför besöket.",
      en: "The difference between a Norwegian offshore certificate and an OEUK medical, with prices and booking information.",
    },
    sections: [
      {
        id: "jamforelse",
        title: { sv: "Två intyg, olika regelverk", en: "Two certificates and different frameworks" },
        paragraphs: {
          sv: ["Din arbetsgivare, operatör eller utbildningsanordnare anger vilket intyg som krävs. Kontrollera exakt formulering innan du planerar besöket. Båda undersökningarna görs enbart fysiskt på vår mottagning i Göteborg."],
          en: ["Your employer, operator or training provider specifies the certificate required. Check the exact wording before arranging a visit. Both examinations take place in person at our Gothenburg clinic."],
        },
        table: {
          columns: { sv: ["Intyg", "Användning och pris"], en: ["Certificate", "Price"] },
          rows: {
            sv: [
              ["Offshore Norge", "Norsk helseerklæring för petroleumverksamhet offshore. 2 500 kr."],
              ["OEUK", "Medicinsk bedömning enligt OEUK för offshorearbete. 5 000 kr."],
            ],
            en: [
              ["Norwegian offshore medical", "2 500 SEK"],
              ["OEUK offshore medical", "5 000 SEK"],
            ],
          },
        },
      },
      {
        id: "samma-moment",
        title: { sv: "Ingår samma undersökningsmoment?", en: "What is included?" },
        paragraphs: {
          sv: [
            "Ja. I båda priserna ingår vikt, längd, midjemått, BMI, blodtryck, urinsticka och läkarbesök. Båda undersökningarna görs enbart fysiskt på vår mottagning i Göteborg.",
            "Intygens medicinska bedömningar följer respektive regelverk. Priset är 2 500 kr för Norge och 5 000 kr för OEUK.",
          ],
          en: [
            "Both packages include weight, height, waist measurement, BMI, blood pressure, urine dipstick and a medical appointment.",
            "The assessments follow the respective frameworks. The price is 2 500 SEK for Norway and 5 000 SEK for OEUK.",
          ],
        },
      },
      {
        id: "oguk",
        title: { sv: "Är OGUK och OEUK samma sak?", en: "OGUK and OEUK" },
        paragraphs: {
          sv: ["OEUK är det nuvarande namnet på organisationen som tidigare hette OGUK. Om arbetsgivaren använder den äldre benämningen OGUK medical är OEUK-sidan rätt utgångspunkt. Ta med arbetsgivarens krav när vi planerar besöket."],
          en: ["OEUK is the current name of the organisation previously called OGUK. Your employer may still use the older name."],
        },
      },
      {
        id: "andra-lander",
        title: { sv: "Kan ett OEUK-intyg användas i Norge?", en: "Acceptance in Norway" },
        paragraphs: {
          sv: [
            "Ett standardintyg enligt OEUK accepteras normalt även i Norge och Nederländerna genom överenskommelser. Platsbundna eller vissa begränsade intyg omfattas inte av samma ordning.",
            "Kontrollera vilket intyg arbetsgivaren eller operatören begär för just ditt arbete. Vi hjälper dig att gå igenom detta innan besöket planeras.",
          ],
          en: [
            "Acceptance depends on the applicable rules and the work. Check the exact certificate requested with the employer or operator, including any limitations or extra training requirements.",
            "Contact us with the instructions if you are unsure.",
          ],
        },
      },
      {
        id: "val",
        title: { sv: "Vad gör jag om jag är osäker?", en: "Not sure which to choose?" },
        paragraphs: {
          sv: ["Skicka en kontaktförfrågan för det intyg du tror att du behöver. När vi återkommer går vi igenom arbetsgivarens krav och bokar rätt undersökning tillsammans med dig."],
          en: ["Send a contact enquiry for the certificate you think you need. When we get back to you, we review the employer's requirements and book the right examination together."],
        },
        link: {
          href: "/lakarintyg/offshore#kontaktformular",
          label: { sv: "Kontakta oss om Offshore Norge", en: "Contact us about Norwegian offshore" },
        },
      },
    ],
    source: {
      sv: "Faktaunderlag: OEUK:s frågor och svar om erkännande av intyg samt Statsforvaltaren i Rogaland. Kontrollerat 8 september 2026.",
      en: "Source: OEUK FAQs on certificate recognition and Statsforvaltaren in Rogaland. Checked 8 September 2026.",
    },
    relatedCert: "oguk",
  },

  // ── DIABETES ──────────────────────────────────────────────────────────────
  {
    category: "diabetes", slug: "krav-och-bedomning",
    label: { sv: "Diabetes och körkort", en: "Requirements and medical assessment" },
    title: { sv: "Diabetes och körkort – vad behöver bedömas?", en: "Diabetes and driving licence – what needs assessing?" },
    description: {
      sv: "Om behandlingen, körkortsbehörigheten och underlagen som läkaren behöver vid ditt besök.",
      en: "About treatment, licence category and the documents the doctor needs at your visit.",
    },
    sections: [
      {
        id: "behandling",
        title: { sv: "Behandlingen har betydelse", en: "The individual medical assessment" },
        paragraphs: {
          sv: ["Bedömningen utgår bland annat från läkemedelsbehandlingen, blodsockerkontrollen och eventuella komplikationer. Risken för hypoglykemi, alltså lågt blodsocker, är särskilt relevant. Läkaren kan behöva uppgifter om tidigare episoder och hur du känner igen varningssignaler."],
          en: ["The assessment considers your medication, blood glucose control and any complications. The risk of hypoglycaemia is particularly relevant. The doctor may need information about previous episodes and how you recognise warning signs."],
        },
      },
      {
        id: "behorighet",
        title: { sv: "Kraven beror också på behörigheten", en: "The licence category matters" },
        paragraphs: {
          sv: ["Reglerna skiljer sig mellan personbil, högre behörigheter och taxi. Diabetes innebär inte automatiskt att körkort är uteslutet. Förutsättningarna bedöms individuellt."],
          en: ["Requirements differ between car, truck, bus and taxi licences. Diabetes does not automatically exclude a driving licence. Circumstances are assessed individually."],
        },
      },
      {
        id: "uppfoljning",
        title: { sv: "Har du fått krav på ett nytt intyg?", en: "Supporting information" },
        paragraphs: {
          sv: [
            "Transportstyrelsen kan besluta att du ska lämna återkommande läkarintyg. Följ den tidsfrist och de frågor som står i ditt beslut.",
            "Boka din tid direkt hos oss. Ha brevet och relevanta uppgifter från din diabetesmottagning tillgängliga vid besöket. Läkaren går igenom dem och talar om ifall något behöver kompletteras.",
          ],
          en: [
            "The Swedish Transport Agency may require recurring medical certificates. Follow the deadline and questions in your decision letter.",
            "Book directly with us. Have the letter and relevant information from your diabetes clinic available. The doctor will review them and advise if anything needs to be supplemented.",
          ],
        },
        link: {
          href: "/guider/diabetes/underlag-och-besok",
          label: { sv: "Vilka dokument ska jag förbereda?", en: "Documents to prepare" },
        },
      },
      {
        id: "beslut",
        title: { sv: "Vem beslutar om körkortet?", en: "The decision and next step" },
        paragraphs: {
          sv: ["Läkarintyget är ett medicinskt underlag. Transportstyrelsen fattar beslut i körkortsärendet efter att underlaget har bedömts."],
          en: ["The medical certificate is supporting evidence. The Swedish Transport Agency decides the driving licence case after reviewing the documentation."],
        },
      },
    ],
    source: {
      sv: "Faktaunderlag: Transportstyrelsens medicinföreskrifter, kapitel 6 om diabetes. Kontrollerat 8 september 2026.",
      en: "Source: Swedish Transport Agency medical regulations, Chapter 6 on diabetes. Checked 8 September 2026.",
    },
    relatedCert: "korkort-diabetes",
    bookingHref: "/boka-tid?intyg=korkort-diabetes",
  },

  {
    category: "diabetes", slug: "underlag-och-besok",
    label: { sv: "Bokning, pris och förberedelser", en: "Documents and appointments" },
    title: { sv: "Diabetes – boka och förbered ditt besök", en: "Diabetes driving licence certificate – booking and preparation" },
    description: {
      sv: "Boka din tid direkt. Här hittar du pris, besöksalternativ och vad du behöver ha till hands.",
      en: "Book your appointment directly. Price, visit options and what to have ready.",
    },
    sections: [
      {
        id: "bokning",
        title: { sv: "Hur bokar jag?", en: "Arrange your appointment" },
        paragraphs: {
          sv: ["Tryck på Boka tid, välj videobesök eller fysiskt besök och fortsätt till kalendern. Välj rätt intyg och en ledig tid. Bokningen sker via vårt bokningssystem HLTHY."],
          en: ["Press Book appointment, choose video or in-person and continue to the calendar. Select the correct certificate and an available time."],
        },
        link: {
          href: "/boka-tid?intyg=korkort-diabetes",
          label: { sv: "Boka tid", en: "Book an appointment" },
        },
      },
      {
        id: "pris",
        title: { sv: "Vad kostar intyget?", en: "Price and what is included" },
        paragraphs: {
          sv: ["Läkarbedömning och körkortsintyg vid diabetes kostar 1 995 kr. Priset är fast och gäller både video och fysiskt besök."],
          en: ["The medical assessment and driving licence certificate for diabetes costs 1 995 SEK. The fixed price applies to both video and in-person visits."],
        },
      },
      {
        id: "besok",
        title: { sv: "Video eller besök på mottagningen?", en: "Video or clinic?" },
        paragraphs: {
          sv: [
            "Du väljer besöksform när du bokar. Vid video träffar du läkaren via mobil eller dator. Fysiska besök sker på mottagningen i Göteborg.",
            "Video passar när ärendet kan bedömas digitalt med tillgängliga underlag. Läkaren avgör om ytterligare medicinska uppgifter eller undersökningar behövs för att färdigställa intyget.",
          ],
          en: [
            "Choose your visit type when booking. Video is via mobile or computer. In-person visits take place at our Gothenburg clinic.",
            "Video works when the case can be assessed digitally with available records. The doctor decides if additional information or examinations are needed.",
          ],
        },
      },
      {
        id: "dokument",
        title: { sv: "Vad behöver jag förbereda?", en: "Have these ready" },
        paragraphs: { sv: [], en: [] },
        items: {
          sv: [
            "Brevet från Transportstyrelsen med samtliga sidor och eventuell sista svarsdag.",
            "Befintligt intyg eller den blankett du har fått.",
            "Ha aktuell läkemedelslista och uppgifter från din diabetesuppföljning tillgängliga om vi behöver komplettera.",
          ],
          en: [
            "All pages of the Swedish Transport Agency's letter and any deadline.",
            "An existing certificate or the form you have received.",
            "Current medication list and information from your diabetes clinic if supplementary information is needed.",
          ],
        },
      },
      {
        id: "intyg",
        title: { sv: "När blir intyget klart?", en: "After the assessment" },
        paragraphs: {
          sv: ["Intyget färdigställs när läkarbedömningen är gjord och nödvändiga uppgifter finns. Om något saknas får du veta vad som behöver kompletteras. Tänk på sista svarsdag i ditt brev när du väljer besökstid."],
          en: ["The certificate is completed when the assessment is done and the necessary information is available. If anything is missing, you will be told what needs to be supplemented. Note the deadline in your letter when choosing a time."],
        },
        link: {
          href: "/boka-tid?intyg=korkort-diabetes",
          label: { sv: "Hitta en ledig tid", en: "Find an available time" },
        },
      },
    ],
    source: {
      sv: "Praktisk information om Intygsläkarens bokning, priser och förberedelser inför läkarbesöket.",
      en: "Practical information on booking, prices and preparation at Intygsläkaren.",
    },
    relatedCert: "korkort-diabetes",
    bookingHref: "/boka-tid?intyg=korkort-diabetes",
  },

  // ── HJÄRT- OCH KÄRL ───────────────────────────────────────────────────────
  {
    category: "hjarta-karl", slug: "krav-och-bedomning",
    label: { sv: "Hjärt- och kärl och körkort", en: "Requirements and medical assessment" },
    title: { sv: "Hjärt- och kärlsjukdom – bedömning för körkort", en: "Heart and vascular disease – driving licence assessment" },
    description: {
      sv: "Så påverkar frågeställningen vilket medicinskt underlag som behövs.",
      en: "How the questions in your case affect which medical documentation is needed.",
    },
    sections: [
      {
        id: "tillstand",
        title: { sv: "Olika tillstånd behöver olika bedömningar", en: "The individual medical assessment" },
        paragraphs: {
          sv: ["Frågan kan exempelvis gälla rytmrubbning, hjärtsvikt, genomgången hjärtinfarkt eller pacemaker. Bedömningen tar hänsyn till symtom, behandling och risken att tillståndet påverkar förmågan att köra säkert."],
          en: ["The question may concern, for example, arrhythmia, heart failure, a previous heart attack or a pacemaker. The assessment considers symptoms, treatment and the risk that the condition affects the ability to drive safely."],
        },
      },
      {
        id: "behorighet",
        title: { sv: "Personbil, lastbil, buss eller taxi?", en: "The licence category matters" },
        paragraphs: {
          sv: ["Medicinska krav kan skilja sig mellan behörigheterna. Även observationstid och behov av uppföljning kan variera. Ett tidigare intyg för personbil räcker därför inte alltid för en annan behörighet."],
          en: ["Requirements can differ between car, truck, bus and taxi licences. Follow the questions and deadline in the Transport Agency's letter; a previous certificate may not answer the current request."],
        },
      },
      {
        id: "specialist",
        title: { sv: "Behövs underlag från hjärtmottagningen?", en: "Supporting information" },
        paragraphs: {
          sv: [
            "I vissa ärenden behövs kardiologisk utredning eller ett särskilt specialistutlåtande. Ha relevanta utlåtanden och Transportstyrelsens frågeställning tillgängliga vid besöket.",
            "Läkaren går igenom vilka uppgifter som behövs och om något behöver kompletteras för att intyget ska kunna färdigställas.",
          ],
          en: [
            "Some cases require cardiac investigation or a specialist report. Have relevant reports and the Transport Agency's questions available at the appointment.",
            "The doctor reviews what information is needed and whether anything must be supplemented to complete the certificate.",
          ],
        },
        link: {
          href: "/guider/hjarta-karl/underlag-och-besok",
          label: { sv: "Läs om underlag och förberedelser", en: "Documents and preparation" },
        },
      },
      {
        id: "intyg",
        title: { sv: "Från medicinsk bedömning till intyg", en: "The decision and next step" },
        paragraphs: {
          sv: ["Du bokar ditt besök direkt. Läkaren behöver sedan ha tillräckligt underlag för att göra bedömningen och färdigställa intyget. Transportstyrelsen beslutar i körkortsärendet."],
          en: ["Book directly. The doctor needs sufficient information to make the assessment and complete the certificate. The Transport Agency decides the driving licence case."],
        },
      },
    ],
    source: {
      sv: "Faktaunderlag: Transportstyrelsens medicinföreskrifter, kapitel 5 om hjärt- och kärlsjukdomar. Kontrollerat 8 september 2026.",
      en: "Source: Swedish Transport Agency medical regulations, Chapter 5 on cardiovascular disease. Checked 8 September 2026.",
    },
    relatedCert: "korkort-hjarta",
    bookingHref: "/boka-tid?intyg=korkort-hjarta",
  },

  {
    category: "hjarta-karl", slug: "underlag-och-besok",
    label: { sv: "Bokning, pris och förberedelser", en: "Documents and appointments" },
    title: { sv: "Hjärt- och kärl – boka och förbered ditt besök", en: "Heart and vascular – booking and preparation" },
    description: {
      sv: "Boka din tid direkt. Pris, besöksalternativ och förberedelser.",
      en: "Book your appointment directly. Price, visit options and what to have ready.",
    },
    sections: [
      {
        id: "bokning",
        title: { sv: "Hur bokar jag?", en: "Arrange your appointment" },
        paragraphs: {
          sv: ["Tryck på Boka tid, välj videobesök eller fysiskt besök och fortsätt till kalendern. Välj rätt intyg och en ledig tid."],
          en: ["Press Book appointment, choose video or in-person and continue to the calendar. Select the correct certificate and an available time."],
        },
        link: {
          href: "/boka-tid?intyg=korkort-hjarta",
          label: { sv: "Boka tid", en: "Book an appointment" },
        },
      },
      {
        id: "pris",
        title: { sv: "Vad kostar intyget?", en: "Price and what is included" },
        paragraphs: {
          sv: ["Läkarbedömning och körkortsintyg vid hjärt- och kärlsjukdom kostar 1 995 kr. Priset är fast och gäller både video och fysiskt besök."],
          en: ["The medical assessment and certificate for cardiovascular conditions costs 1 995 SEK. The fixed price applies to both video and in-person visits."],
        },
      },
      {
        id: "besok",
        title: { sv: "Video eller besök på mottagningen?", en: "Video or clinic?" },
        paragraphs: {
          sv: [
            "Du väljer besöksform när du bokar. Vid video träffar du läkaren via mobil eller dator. Fysiska besök sker på mottagningen i Göteborg.",
            "Video passar när ärendet kan bedömas digitalt. Läkaren avgör om ytterligare undersökningar eller uppgifter behövs.",
          ],
          en: [
            "Choose your visit type when booking. Video is via mobile or computer; in-person at our Gothenburg clinic.",
            "Video works when the case can be assessed digitally. The doctor decides if additional examinations or information are needed.",
          ],
        },
      },
      {
        id: "dokument",
        title: { sv: "Vad behöver jag förbereda?", en: "Have these ready" },
        paragraphs: { sv: [], en: [] },
        items: {
          sv: [
            "Transportstyrelsens brev eller föreläggande med hela frågeställningen.",
            "Tidigare intyg, om du har ett.",
            "Ha läkemedelslista och relevanta utlåtanden från din hjärtmottagning tillgängliga. Vi säger till om något behöver kompletteras.",
          ],
          en: [
            "The Swedish Transport Agency's letter with all questions and any deadline.",
            "Previous certificates, if available.",
            "Medication list and relevant reports from your cardiology clinic. We will advise if anything needs to be supplemented.",
          ],
        },
      },
      {
        id: "intyg",
        title: { sv: "När blir intyget klart?", en: "After the assessment" },
        paragraphs: {
          sv: ["Intyget färdigställs när läkarbedömningen är gjord och nödvändiga uppgifter finns. Om något saknas får du veta vad som behöver kompletteras."],
          en: ["The certificate is completed when the assessment is done and necessary information is available. If anything is missing, you will be told what needs to be supplemented."],
        },
      },
    ],
    source: {
      sv: "Praktisk information om Intygsläkarens bokning, priser och förberedelser inför läkarbesöket.",
      en: "Practical information on booking, prices and preparation at Intygsläkaren.",
    },
    relatedCert: "korkort-hjarta",
    bookingHref: "/boka-tid?intyg=korkort-hjarta",
  },

  // ── ÖVRIGA TILLSTÅND ──────────────────────────────────────────────────────
  {
    category: "ovriga-tillstand", slug: "krav-och-bedomning",
    label: { sv: "Bedömning av ditt ärende", en: "Requirements and medical assessment" },
    title: { sv: "Övriga medicinska tillstånd – kan vi hjälpa dig?", en: "Driving licences and other medical conditions" },
    description: {
      sv: "Börja med det intyg eller brev du har. Du behöver inte själv välja rätt medicinsk specialitet.",
      en: "Start with the document you have. You do not need to identify the correct medical specialty yourself.",
    },
    sections: [
      {
        id: "vem",
        title: { sv: "När väljer jag övriga tillstånd?", en: "When to use this option" },
        paragraphs: {
          sv: ["Välj övriga tillstånd när ditt körkortsärende gäller något annat än diabetes eller hjärt- och kärlsjukdom. Du kan också välja det här alternativet om brevet innehåller flera medicinska frågor och du är osäker på vilket intyg som behövs."],
          en: ["Choose other conditions when your driving licence case concerns something other than diabetes or cardiovascular disease. You can also use this if the letter contains several medical questions and you are unsure which certificate is needed."],
        },
      },
      {
        id: "krav",
        title: { sv: "Vi börjar med det som efterfrågas", en: "Send the full requirements" },
        paragraphs: {
          sv: ["Det viktigaste för vår första genomgång är att se själva frågeställningen. Skicka hela brevet, blanketten eller det befintliga intyget. Om dokumentet anger att en särskild specialist ska skriva intyget behöver även den sidan finnas med."],
          en: ["The most important thing for our initial review is to see the actual question. Send the full letter, form or existing certificate. If the document states that a particular specialist is required, that page must also be included."],
        },
      },
      {
        id: "hjalp",
        title: { sv: "Så får du veta om vi kan hjälpa till", en: "Finding out if we can help" },
        paragraphs: {
          sv: ["Vi går igenom underlaget och kontaktar dig. Ibland behöver vi fler uppgifter innan vi kan ge besked. Om ärendet kräver en annan specialist berättar vi det; vi lovar inte att alla typer av intyg kan utfärdas hos oss."],
          en: ["We review the documentation and contact you. Sometimes we need more information before giving an answer. If the case requires a different specialist, we will tell you; we do not promise that all types of certificate can be issued with us."],
        },
      },
      {
        id: "pris",
        title: { sv: "Pris och besök bestäms efter genomgång", en: "Price and next step" },
        paragraphs: {
          sv: ["Eftersom olika ärenden kräver olika arbete lämnar vi pris när vi har läst underlaget. Vi kommer också överens om lämplig besöksform om vi kan gå vidare. Formuläret bokar ingen tid."],
          en: ["Because different cases require different work, we provide a price after reviewing the documentation. We also agree on the appropriate visit type if we can proceed. Submitting the form does not book an appointment."],
        },
        link: {
          href: "/guider/ovriga-tillstand/underlag-och-besok",
          label: { sv: "Så skickar du underlag och får återkoppling", en: "How to send documents and get feedback" },
        },
      },
    ],
    source: {
      sv: "Så fungerar den första genomgången hos Intygsläkaren.",
      en: "How the initial review works at Intygsläkaren.",
    },
    relatedCert: "korkort-adhd",
  },

  {
    category: "ovriga-tillstand", slug: "underlag-och-besok",
    label: { sv: "Underlag, pris och besök", en: "Documents and appointments" },
    title: { sv: "Övriga tillstånd – skicka underlag och planera nästa steg", en: "Other conditions – send documents and plan next steps" },
    description: {
      sv: "Vad du kan bifoga, hur vi återkommer och när pris och besöksform bestäms.",
      en: "What to attach, how we respond and when price and visit type are confirmed.",
    },
    sections: [
      {
        id: "dokument",
        title: { sv: "Vad ska jag bifoga?", en: "Send your documents first" },
        paragraphs: {
          sv: ["Skicka det dokument som visar vad ditt körkortsärende gäller. Det kan vara ett intyg du redan har, en blankett som ska fyllas i eller ett föreläggande från Transportstyrelsen."],
          en: ["Send the document that shows what your driving licence case is about. It may be a certificate you already have, a form to be completed or a decision from the Swedish Transport Agency."],
        },
        items: {
          sv: [
            "Blanketten, intyget eller brevet som visar vad som efterfrågas.",
            "Alla sidor med frågor och eventuella krav på läkarens specialistkompetens.",
            "Sista svarsdag, om den finns angiven.",
          ],
          en: [
            "The form, certificate or letter showing what is required.",
            "All pages, including any requirements for the doctor's specialty.",
            "The deadline, if one has been specified.",
          ],
        },
      },
      {
        id: "fil",
        title: { sv: "Så förbereder du filen", en: "Prepare your file" },
        paragraphs: {
          sv: [
            "Formuläret tar emot en PDF, JPG eller PNG på högst 10 MB. Har dokumentet flera sidor kan du samla dem i en PDF. Kontrollera att hela sidan syns och att texten är skarp och läsbar.",
            "Du behöver inte skicka hela din journal i den första förfrågan. Börja med underlaget som beskriver intygsbehovet. Vi återkommer om något mer behövs.",
          ],
          en: [
            "Send one PDF, JPG or PNG, up to 10 MB. Combine multiple pages into one PDF if helpful. Make sure the whole document is visible and the text is sharp and readable.",
            "You do not need to send your entire medical record. Start with the document explaining the certificate requirements; we will ask for more if needed.",
          ],
        },
      },
      {
        id: "aterkoppling",
        title: { sv: "Vad händer när jag har skickat?", en: "After submitting" },
        paragraphs: {
          sv: [
            "En bekräftelse visas när både kontaktuppgifter och fil har tagits emot. Underlaget blir tillgängligt för mottagningen i en skyddad lista. Vi går igenom frågeställningen och kontaktar dig på de uppgifter du lämnar.",
            "Om ett fel visas finns dina uppgifter och ditt filval kvar i formuläret så länge du stannar på sidan. Försök igen.",
          ],
          en: [
            "Confirmation is shown when both contact details and file have been received. The documentation becomes available to the clinic in a protected list. We review the case and contact you.",
            "If an error is shown, your details and file selection remain in the form while you stay on the page. Try again.",
          ],
        },
      },
      {
        id: "pris",
        title: { sv: "När får jag veta priset?", en: "When do I find out the price?" },
        paragraphs: {
          sv: ["Vi lämnar pris efter att ha gått igenom vad som behövs i ditt ärende. Du får besked om upplägg och eventuella kompletterande undersökningar innan du bokar. Skicka gärna sista svarsdag från brevet; vi bekräftar vad som är möjligt när vi återkommer."],
          en: ["We provide a price after reviewing what is needed in your case. You will be informed of the approach and any additional examinations before booking. Include the deadline from your letter; we confirm what is possible when we get back to you."],
        },
        link: {
          href: "/lakarintyg/lakarintyg-korkort-adhd-add-autism#skicka-underlag",
          label: { sv: "Till formuläret – skicka ditt underlag", en: "Send your documents" },
        },
      },
    ],
    source: {
      sv: "Praktisk information om Intygsläkarens förfrågan, dokumenthantering och planering av intygsbesök.",
      en: "Practical information on enquiries, document handling and appointment planning at Intygsläkaren.",
    },
    relatedCert: "korkort-adhd",
  },

  // ── FÖRSTADAGSINTYG ───────────────────────────────────────────────────────
  {
    category: "forstadagsintyg", slug: "intyg-till-arbetsgivaren",
    label: { sv: "Intyg till arbetsgivaren", en: "A certificate for your employer" },
    title: { sv: "Förstadagsintyg – när behövs det?", en: "First-day certificate – when is it needed?" },
    description: {
      sv: "Vad ett förstadagsintyg innebär och vad läkaren behöver bedöma.",
      en: "What a first-day sickness certificate involves and what the doctor needs to assess.",
    },
    sections: [
      {
        id: "innebord",
        title: { sv: "Ett intyg om arbetsförmåga vid sjukdom", en: "A certificate about your ability to work" },
        paragraphs: {
          sv: ["Ett förstadagsintyg används när nedsatt arbetsförmåga behöver styrkas redan från den första sjukdagen. Läkaren bedömer hur sjukdomen påverkar möjligheten att utföra dina arbetsuppgifter."],
          en: ["A first-day certificate is used when reduced ability to work needs to be confirmed from the first day of illness. The doctor assesses how the illness affects your ability to carry out your duties."],
        },
      },
      {
        id: "begaran",
        title: { sv: "När kan arbetsgivaren begära det?", en: "The employer's request" },
        paragraphs: {
          sv: [
            "Enligt sjuklönelagen krävs särskilda skäl för att arbetsgivaren ska kunna begära intyg tidigare i sjukperioden. Begäran ska vara skriftlig. Gäller den kommande sjukperioder får den omfatta högst ett år. Kollektivavtal kan innehålla andra bestämmelser.",
            "Ha den begäran du har fått tillgänglig vid läkarbesöket, tillsammans med en eventuell blankett.",
          ],
          en: [
            "Under the Sick Pay Act, special reasons are required before an employer can request a certificate earlier in a sick period. The request must be in writing. If it covers future sick periods, it may cover at most one year. Collective agreements may contain different provisions.",
            "Have the request you have received available at the appointment, together with any form.",
          ],
        },
      },
      {
        id: "sjukdag",
        title: { sv: "När ska jag boka?", en: "When to book" },
        paragraphs: {
          sv: ["Boka gärna den dag du blir sjuk om du har krav på förstadagsintyg. Berätta när besvären började och vilken dag du varit frånvarande från arbetet. Läkaren avgör vad som kan bedömas och styrkas med det underlag som finns."],
          en: ["Try to book on the day you fall ill if a first-day certificate is required. Tell the doctor when the symptoms started and which day you were absent from work. The doctor decides what can be assessed and certified with available information."],
        },
      },
      {
        id: "innehall",
        title: { sv: "Vad får arbetsgivaren veta?", en: "Information for the employer" },
        paragraphs: {
          sv: ["Intyget beskriver hur arbetsförmågan är nedsatt. Ett sjuklöneintyg behöver normalt inte ange diagnosen. Arbetsgivaren använder intyget som underlag för sin bedömning av rätten till sjuklön."],
          en: ["The certificate describes the reduction in ability to work. A sick pay certificate does not normally need to state the diagnosis. The employer uses the certificate as the basis for assessing the right to sick pay."],
        },
      },
      {
        id: "online",
        title: { sv: "Kan jag träffa läkaren online?", en: "Video or clinic?" },
        paragraphs: {
          sv: ["Ja. Hos oss kan du boka videobesök eller ett fysiskt besök i Göteborg. Läkarbedömning och intyg kostar 1 000 kr."],
          en: ["Yes. You can book a video consultation or an in-person visit in Gothenburg. The medical assessment and certificate cost 1 000 SEK."],
        },
        link: {
          href: "/guider/forstadagsintyg/infor-besoket",
          label: { sv: "Läs om bokning och förberedelser", en: "Booking and preparation" },
        },
      },
    ],
    source: {
      sv: "Faktaunderlag: sjuklönelagen, 8 och 10 a §§, samt Försäkringskassans information om sjuklön. Kontrollerat 8 september 2026.",
      en: "Source: Sick Pay Act, sections 8 and 10a, and the Swedish Social Insurance Agency information on sick pay. Checked 8 September 2026.",
    },
    relatedCert: "forstadagsintyg",
    bookingHref: "/boka-tid?intyg=forstadagsintyg",
  },

  {
    category: "forstadagsintyg", slug: "infor-besoket",
    label: { sv: "Bokning och förberedelser", en: "Prepare your appointment" },
    title: { sv: "Boka och förbered ditt förstadagsintyg", en: "Booking and preparation for your first-day certificate" },
    description: {
      sv: "Onlinebesök eller mottagning, pris och vilka uppgifter du behöver ha redo.",
      en: "Online or clinic visit, price and what information to have ready.",
    },
    sections: [
      {
        id: "boka",
        title: { sv: "Boka ett läkarbesök", en: "Arrange your appointment" },
        paragraphs: {
          sv: ["Välj Boka tid, välj videobesök eller mottagningsbesök och fortsätt till kalendern. Vid video träffar du läkaren via mobil eller dator. Fysiska besök sker i Göteborg."],
          en: ["Choose Book appointment, select video or clinic and continue to the calendar. Video is via mobile or computer; in-person visits are in Gothenburg."],
        },
        link: {
          href: "/boka-tid?intyg=forstadagsintyg",
          label: { sv: "Boka förstadagsintyg", en: "Book a first-day certificate" },
        },
      },
      {
        id: "underlag",
        title: { sv: "Vad behöver jag ha till hands?", en: "Have these ready" },
        paragraphs: {
          sv: ["Följ instruktionerna i bokningen för hur underlagen ska lämnas. Beskriv gärna vilka arbetsuppgifter som blir svåra att utföra på grund av besvären."],
          en: ["Follow the booking instructions on how to submit documents. Describe which duties are difficult to carry out because of your symptoms."],
        },
        items: {
          sv: [
            "Giltig legitimation.",
            "Arbetsgivarens begäran om förstadagsintyg och eventuell blankett.",
            "Uppgifter om dina arbetsuppgifter, symtom och första sjukdag.",
            "Aktuell läkemedelslista och relevanta medicinska uppgifter.",
          ],
          en: [
            "Valid photo ID.",
            "The employer's written request for a first-day certificate and any form.",
            "Details of your duties, symptoms and first day of illness.",
            "Current medication list and relevant medical information.",
          ],
        },
      },
      {
        id: "pris",
        title: { sv: "Vad kostar det?", en: "Price and what is included" },
        paragraphs: {
          sv: ["Priset är 1 000 kr för läkarbedömning och intyg. Stäm av med arbetsgivaren vad som gäller för betalningen i ditt fall."],
          en: ["The price is 1 000 SEK for the medical assessment and certificate. Check with your employer what applies for payment in your case."],
        },
      },
      {
        id: "komplettering",
        title: { sv: "Om ytterligare undersökningar behövs", en: "During the assessment" },
        paragraphs: {
          sv: [
            nationwideInv.sv,
            "Undersökningar planeras när läkaren bedömer att de behövs. Prover och röntgen görs på en anvisad mottagning. Du får information om upplägg och eventuell kostnad innan en komplettering planeras.",
          ],
          en: [
            nationwideInv.en,
            "Additional examinations are planned when the doctor considers them necessary. Tests and X-rays are carried out at an assigned clinic. You will be informed of the arrangement and any costs before they are planned.",
          ],
        },
      },
      {
        id: "efter",
        title: { sv: "Efter läkarbedömningen", en: "After the assessment" },
        paragraphs: {
          sv: ["Läkaren tar ställning till om arbetsförmågan är nedsatt och om ett intyg kan utfärdas. Om underlaget behöver kompletteras får du veta vad som saknas. Följ arbetsgivarens instruktioner för att lämna in intyget."],
          en: ["The doctor decides whether your ability to work is reduced and whether a certificate can be issued. If the documentation needs supplementing, you will be told what is missing. Follow the employer's instructions for submitting the certificate."],
        },
      },
    ],
    source: {
      sv: "Praktisk information om Intygsläkarens förstadagsintyg, bokning och kompletterande undersökningar.",
      en: "Practical information on first-day certificates, booking and supplementary examinations at Intygsläkaren.",
    },
    relatedCert: "forstadagsintyg",
    bookingHref: "/boka-tid?intyg=forstadagsintyg",
  },

  // ── UTLANDSSTUDIER ARBETE ─────────────────────────────────────────────────
  {
    category: "utlandsstudier-arbete", slug: "blankett-och-intygskrav",
    label: { sv: "Blankett och intygskrav", en: "Forms and certificate requirements" },
    title: { sv: "Intyg för utlandsstudier eller arbete – vad behövs?", en: "Medical certificate for study or work abroad – what is required?" },
    description: {
      sv: "Mottagarens instruktioner visar vad ditt intyg ska innehålla. Här är det viktigaste att förbereda.",
      en: "The recipient's instructions determine what your certificate must contain. Key things to prepare.",
    },
    sections: [
      {
        id: "mottagare",
        title: { sv: "Utgå från rätt blankett", en: "Use the current form" },
        paragraphs: {
          sv: ["Be skolan, arbetsgivaren eller den som ska ta emot intyget om den aktuella blanketten. Olika mottagare kan efterfråga olika medicinska uppgifter och undersökningar."],
          en: ["Ask the school, employer or receiving party for the current form. Different recipients may request different medical information and examinations."],
        },
      },
      {
        id: "krav",
        title: { sv: "Kontrollera språk, datum och undersökningskrav", en: "Prepare the right documents" },
        paragraphs: {
          sv: [
            "Ta med information om vilket språk intyget ska vara på, när det senast ska lämnas och om särskilda prover eller röntgenundersökningar efterfrågas. Läkaren går igenom kraven med dig vid besöket.",
            "Använd mottagarens aktuella instruktioner. Ett tidigare intyg eller en annan organisations blankett behöver inte täcka samma frågor.",
          ],
          en: [
            "Bring information about the required language, the deadline and whether specific tests or X-rays are requested. The doctor reviews the requirements with you at the appointment.",
            "Use the recipient's current instructions. A previous certificate or another organisation's form may not cover the same questions.",
          ],
        },
      },
      {
        id: "forbered",
        title: { sv: "Din checklista inför besöket", en: "Your checklist before the visit" },
        paragraphs: {
          sv: ["Ha dokumenten tillgängliga och följ bokningens instruktioner för hur de ska lämnas."],
          en: ["Have the documents available and follow the booking instructions on how to submit them."],
        },
        items: {
          sv: [
            "Giltig legitimation och den aktuella intygsblanketten.",
            "Instruktioner om undersökningar, språk och sista inlämningsdag.",
            "Aktuell läkemedelslista och relevanta medicinska underlag.",
            "Tidigare provsvar och vaccinationsuppgifter om mottagaren efterfrågar dem.",
          ],
          en: [
            "Valid photo ID and the current certificate form.",
            "Instructions about examinations, language and the submission deadline.",
            "Current medication list and relevant medical records.",
            "Previous test results and vaccination records if the recipient requests them.",
          ],
        },
      },
      {
        id: "online",
        title: { sv: "Kan intyget göras online?", en: "Can the certificate be done online?" },
        paragraphs: {
          sv: [
            "Ja, läkarbesöket kan göras online. Du väljer videobesök i bokningen. Du kan också träffa läkaren på mottagningen i Göteborg.",
            `${nationwideInv.sv} Eventuella undersökningar görs fysiskt på en anvisad mottagning och resultaten ingår sedan i bedömningen.`,
          ],
          en: [
            "Yes, the doctor's appointment can take place online. You can also visit the clinic in Gothenburg.",
            `${nationwideInv.en} Any required examinations are carried out in person at an assigned clinic; the results then form part of the assessment.`,
          ],
        },
        link: {
          href: "/guider/utlandsstudier-arbete/provtagning-och-rontgen",
          label: { sv: "Läs om provtagning och röntgen i hela Sverige", en: "Tests and X-rays across Sweden" },
        },
      },
      {
        id: "klart",
        title: { sv: "När blir intyget färdigt?", en: "When is the certificate ready?" },
        paragraphs: {
          sv: ["Intyget färdigställs när läkaren har gjort bedömningen och alla nödvändiga uppgifter och resultat finns. Boka med marginal till avresa eller sista inlämningsdag om dokumentet kräver kompletterande undersökningar."],
          en: ["The certificate is completed when the doctor has made the assessment and all necessary information and results are available. Book with time to spare if the document requires supplementary examinations."],
        },
        link: {
          href: "/boka-tid?intyg=utlandsstudier",
          label: { sv: "Boka ditt läkarbesök", en: "Book your appointment" },
        },
      },
    ],
    source: {
      sv: "Praktisk information från Intygsläkaren. Intygets innehåll anpassas efter mottagarens aktuella blankett och läkarens bedömning.",
      en: "Practical information from Intygsläkaren. The certificate content is adapted to the recipient's current form and the doctor's assessment.",
    },
    relatedCert: "utlandsstudier",
    bookingHref: "/boka-tid?intyg=utlandsstudier",
  },

  {
    category: "utlandsstudier-arbete", slug: "provtagning-och-rontgen",
    label: { sv: "Prover, röntgen och pris", en: "Tests, X-rays and price" },
    title: { sv: "Utlandsstudier eller arbete – provtagning och röntgen i hela Sverige", en: "Study or work abroad – tests and X-rays across Sweden" },
    description: {
      sv: "Så kombinerar du ett läkarbesök online med eventuella undersökningar på en mottagning.",
      en: "How to combine an online doctor's appointment with in-person examinations at a clinic.",
    },
    sections: [
      {
        id: "landet",
        title: { sv: "Vi hjälper dig att planera undersökningarna", en: "Tests and X-rays across Sweden" },
        paragraphs: {
          sv: [
            nationwideInv.sv,
            "Du behöver därför inte resa till Göteborg för en kompletterande undersökning enbart för att läkarbesöket har skett online hos oss. Vi går igenom vad som behövs och hjälper dig med var och hur undersökningen ska planeras.",
          ],
          en: [
            nationwideInv.en,
            "You therefore do not need to travel to Gothenburg for a supplementary examination just because the doctor's appointment took place online. We review what is needed and help you plan where and how the examination should take place.",
          ],
        },
      },
      {
        id: "behov",
        title: { sv: "Behöver alla lämna prover eller göra röntgen?", en: "Does everyone need tests?" },
        paragraphs: {
          sv: ["Nej. Behovet avgörs av vad mottagaren efterfrågar och läkarens medicinska bedömning. Ha blanketten tillgänglig så att rätt undersökningar kan planeras från början. Berätta om du redan har relevanta resultat."],
          en: ["No. The need is determined by the recipient's requirements and the doctor's medical assessment. Have the form available so the right examinations can be planned from the start. Tell us if you already have relevant results."],
        },
      },
      {
        id: "pris",
        title: { sv: "Vad kostar intyget och undersökningarna?", en: "Price" },
        paragraphs: {
          sv: ["Läkarbedömning och intyg kostar från 1 599 kr. Provtagning och röntgen ingår inte i frånpriset. Du får information om undersökningarnas kostnad innan de planeras."],
          en: ["The medical assessment and certificate cost from 1 599 SEK. Tests and X-rays are not included in the starting price. You will be informed of examination costs before they are planned."],
        },
      },
      {
        id: "besok",
        title: { sv: "Hur går det till?", en: "From appointment to certificate" },
        paragraphs: {
          sv: [
            "Boka läkarbesöket online eller på mottagningen. Läkaren går igenom blanketten och behovet av kompletteringar. Om prover eller röntgen behövs får du instruktioner för den anvisade mottagningen.",
            "Följ de förberedelser du får för just din undersökning. När resultaten finns går läkaren vidare med bedömningen och intyget.",
          ],
          en: [
            "Book the doctor's appointment online or at the clinic. The doctor reviews the form and the need for supplementary examinations. If tests or X-rays are needed, you receive instructions for the assigned clinic.",
            "Follow the preparation instructions for your examination. When the results are available, the doctor proceeds with the assessment and certificate.",
          ],
        },
        link: {
          href: "/boka-tid?intyg=utlandsstudier",
          label: { sv: "Boka tid", en: "Book an appointment" },
        },
      },
    ],
    source: {
      sv: "Intygsläkarens information om priser och planering av provtagning och röntgen i hela Sverige.",
      en: "Intygsläkaren information on prices and planning of tests and X-rays across Sweden.",
    },
    relatedCert: "utlandsstudier",
    bookingHref: "/boka-tid?intyg=utlandsstudier",
  },

  // ── AU PAIR ───────────────────────────────────────────────────────────────
  {
    category: "au-pair", slug: "blankett-och-intygskrav",
    label: { sv: "Blankett och intygskrav", en: "Forms and certificate requirements" },
    title: { sv: "Au pair-intyg – blankett och förberedelser", en: "Au pair medical certificate – forms and preparation" },
    description: {
      sv: "Ta fram au pair-organisationens blankett och instruktioner. Det viktigaste att ha redo inför besöket.",
      en: "Obtain the au pair organisation's form and instructions. Key things to have ready before your visit.",
    },
    sections: [
      {
        id: "mottagare",
        title: { sv: "Utgå från rätt blankett", en: "Use the current form" },
        paragraphs: {
          sv: ["Ta fram au pair-organisationens aktuella medicinska blankett och samtliga instruktioner. Om värdfamiljen eller mottagaren har särskilda intygskrav behöver även de finnas med."],
          en: ["Obtain the au pair organisation's current medical form and all instructions. If the host family or recipient has additional certificate requirements, these must also be included."],
        },
      },
      {
        id: "krav",
        title: { sv: "Kontrollera språk, datum och undersökningskrav", en: "Prepare the right documents" },
        paragraphs: {
          sv: ["Ta med information om vilket språk intyget ska vara på, när det senast ska lämnas och om särskilda prover eller röntgenundersökningar efterfrågas. Läkaren går igenom kraven med dig vid besöket."],
          en: ["Bring information about the required language, the deadline and whether specific tests or X-rays are requested. The doctor reviews the requirements with you at the appointment."],
        },
      },
      {
        id: "forbered",
        title: { sv: "Din checklista inför besöket", en: "Your checklist before the visit" },
        paragraphs: {
          sv: ["Ha dokumenten tillgängliga och följ bokningens instruktioner för hur de ska lämnas."],
          en: ["Have the documents available and follow the booking instructions."],
        },
        items: {
          sv: [
            "Giltig legitimation.",
            "Au pair-organisationens blankett och samtliga instruktioner.",
            "Uppgifter om destination, avresedatum och sista dag för intyget.",
            "Läkemedelslista samt vaccinationsuppgifter och tidigare provsvar om de efterfrågas.",
          ],
          en: [
            "Valid photo ID.",
            "The au pair organisation's form and all instructions.",
            "Details of destination, departure date and certificate deadline.",
            "Medication list, vaccination records and previous test results if requested.",
          ],
        },
      },
      {
        id: "online",
        title: { sv: "Kan intyget göras online?", en: "Can the certificate be done online?" },
        paragraphs: {
          sv: [
            "Ja, läkarbesöket kan göras online. Du väljer videobesök i bokningen. Du kan också träffa läkaren på mottagningen i Göteborg.",
            `${nationwideInv.sv} Eventuella undersökningar görs fysiskt på en anvisad mottagning.`,
          ],
          en: [
            "Yes, the doctor's appointment can take place online. You can also visit the clinic in Gothenburg.",
            `${nationwideInv.en} Any required examinations are carried out in person at an assigned clinic.`,
          ],
        },
        link: {
          href: "/guider/au-pair/provtagning-och-rontgen",
          label: { sv: "Läs om provtagning och röntgen i hela Sverige", en: "Tests and X-rays across Sweden" },
        },
      },
      {
        id: "klart",
        title: { sv: "När blir intyget färdigt?", en: "When is the certificate ready?" },
        paragraphs: {
          sv: ["Intyget färdigställs när läkaren har gjort bedömningen och alla nödvändiga uppgifter och resultat finns. Boka med marginal till avresa eller sista inlämningsdag om dokumentet kräver kompletterande undersökningar."],
          en: ["The certificate is completed when the doctor has made the assessment and all necessary information and results are available."],
        },
        link: {
          href: "/boka-tid?intyg=au-pair",
          label: { sv: "Boka ditt läkarbesök", en: "Book your appointment" },
        },
      },
    ],
    source: {
      sv: "Praktisk information från Intygsläkaren.",
      en: "Practical information from Intygsläkaren.",
    },
    relatedCert: "au-pair",
    bookingHref: "/boka-tid?intyg=au-pair",
  },

  {
    category: "au-pair", slug: "provtagning-och-rontgen",
    label: { sv: "Prover, röntgen och pris", en: "Tests, X-rays and price" },
    title: { sv: "Au pair – provtagning och röntgen i hela Sverige", en: "Au pair – tests and X-rays across Sweden" },
    description: {
      sv: "Så kombinerar du ett läkarbesök online med eventuella undersökningar på en mottagning.",
      en: "How to combine an online doctor's appointment with in-person examinations at a clinic.",
    },
    sections: [
      {
        id: "landet",
        title: { sv: "Vi hjälper dig att planera undersökningarna", en: "Tests and X-rays across Sweden" },
        paragraphs: {
          sv: [
            nationwideInv.sv,
            "Du behöver inte resa till Göteborg för en kompletterande undersökning enbart för att läkarbesöket skett online. Vi går igenom vad som behövs och hjälper dig med var undersökningen ska planeras.",
          ],
          en: [
            nationwideInv.en,
            "You do not need to travel to Gothenburg for a supplementary examination just because the doctor's appointment took place online.",
          ],
        },
      },
      {
        id: "behov",
        title: { sv: "Behöver alla lämna prover eller göra röntgen?", en: "Does everyone need tests?" },
        paragraphs: {
          sv: ["Nej. Behovet avgörs av vad mottagaren efterfrågar och läkarens medicinska bedömning. Ha blanketten tillgänglig så att rätt undersökningar kan planeras. Berätta om du redan har relevanta resultat."],
          en: ["No. The need is determined by the recipient's requirements and the doctor's assessment. Have the form available so the right examinations can be planned. Tell us if you already have relevant results."],
        },
      },
      {
        id: "pris",
        title: { sv: "Vad kostar intyget och undersökningarna?", en: "Price" },
        paragraphs: {
          sv: ["Läkarbedömning och intyg kostar från 1 500 kr. Provtagning och röntgen ingår inte i frånpriset. Du får information om undersökningarnas kostnad innan de planeras."],
          en: ["The medical assessment and certificate cost from 1 500 SEK. Tests and X-rays are not included in the starting price."],
        },
      },
      {
        id: "besok",
        title: { sv: "Hur går det till?", en: "From appointment to certificate" },
        paragraphs: {
          sv: [
            "Boka läkarbesöket online eller på mottagningen. Läkaren går igenom blanketten och behovet av kompletteringar.",
            "Följ de förberedelser du får för din undersökning. När resultaten finns går läkaren vidare med bedömningen.",
          ],
          en: [
            "Book the doctor's appointment online or at the clinic. The doctor reviews the form and the need for supplementary examinations.",
            "Follow the preparation instructions. When the results are available, the doctor proceeds with the assessment.",
          ],
        },
        link: {
          href: "/boka-tid?intyg=au-pair",
          label: { sv: "Boka tid", en: "Book an appointment" },
        },
      },
    ],
    source: {
      sv: "Intygsläkarens information om priser och planering av provtagning och röntgen i hela Sverige.",
      en: "Intygsläkaren information on prices and planning of tests and X-rays across Sweden.",
    },
    relatedCert: "au-pair",
    bookingHref: "/boka-tid?intyg=au-pair",
  },

  // ── NATTARBETE (from work environment) ────────────────────────────────────
  {
    category: "nattarbete", slug: "regler-och-kontroll",
    label: { sv: "Regler och intervall", en: "Requirements and examination" },
    title: { sv: "Nattarbete – krav och medicinsk kontroll", en: "Night work – medical examination requirements" },
    description: {
      sv: "När kontrollen behövs, vad som undersöks och hur uppföljningen planeras.",
      en: "When the examination is required, what is examined and how follow-up is planned.",
    },
    sections: [
      {
        id: "nar",
        title: { sv: "När behövs kontrollen?", en: "When is the examination required?" },
        paragraphs: {
          sv: ["Som nattarbetande räknas den som normalt arbetar minst tre timmar mellan klockan 22 och 06, eller minst en tredjedel av årsarbetstiden under dessa timmar. Undantag finns om anställningar med nattarbete sammanlagt understiger tre månader under ett år."],
          en: ["A night worker is normally defined as someone who works at least three hours between 22:00 and 06:00, or at least one third of annual working hours during these times. Exceptions apply when employment involving night work totals less than three months per year."],
        },
      },
      {
        id: "intervall",
        title: { sv: "När ska den göras och upprepas?", en: "Timing and repeat examinations" },
        paragraphs: {
          sv: ["Vid nyanställning ska kontrollen normalt göras innan nattarbetet börjar. Vid byte från dagarbete ska den göras inom tre månader. Därefter gäller högst sex år mellan kontrollerna före 50 års ålder och högst tre år från 50 års ålder."],
          en: ["For new employees, the examination should normally be done before night work begins. When switching from day work it should be done within three months. Thereafter intervals are at most six years under 50 and at most three years from age 50."],
        },
        table: {
          columns: { sv: ["Ålder", "Återkommande kontroll"], en: ["Age", "Repeat examination"] },
          rows: {
            sv: [["Under 50 år", "Högst sex år mellan kontrollerna"], ["50 år och äldre", "Högst tre år mellan kontrollerna"]],
            en: [["Under 50", "At most every six years"], ["Age 50 and over", "At most every three years"]],
          },
        },
      },
      {
        id: "undersokning",
        title: { sv: "Vad görs vid kontrollen?", en: "What is examined?" },
        paragraphs: {
          sv: ["Vid besöket går läkaren igenom din hälsa, dina arbetstider, sömn och återhämtning. Kontrollens syfte är att bedöma risk för ohälsa i samband med nattarbete."],
          en: ["The doctor reviews your health, working hours, sleep and recovery. The purpose of the examination is to assess the risk of ill health associated with night work."],
        },
        items: {
          sv: ["Fysiskt läkarbesök i Göteborg", "Genomgång av hälsa, sömn och arbetstider", "Blodtryckskontroll", "Provtagning enligt kontrollens upplägg", "Medicinsk bedömning och återkoppling"],
          en: ["In-person medical appointment in Gothenburg", "Review of health, sleep and working hours", "Blood pressure check", "Testing according to the examination plan", "Medical assessment and feedback"],
        },
      },
      {
        id: "intyg",
        title: { sv: "Kontroll och tjänstbarhetsintyg", en: "Examination and fitness certificate" },
        paragraphs: {
          sv: ["Nattarbetskontrollen kräver inget tjänstbarhetsintyg enligt AFS 2023:15. Du får en medicinsk bedömning och råd utifrån dina arbetsförhållanden."],
          en: ["The night work examination does not require a certificate of fitness for work under AFS 2023:15. You receive a medical assessment and advice based on your working conditions."],
        },
      },
      {
        id: "arbetsgivare",
        title: { sv: "Arbetsgivarens underlag behövs", en: "Information from the employer" },
        paragraphs: {
          sv: ["Arbetsgivarens riskbedömning visar vilka arbetsmoment och exponeringar som behöver bedömas. Kontrollen behöver kopplas till det faktiska arbetet. Ha beställningen tillgänglig och följ checklistan inför besöket."],
          en: ["The employer's risk assessment shows which work tasks and exposures need to be assessed. The examination must be linked to the actual work. Have the work order available and follow the checklist before the visit."],
        },
        link: {
          href: "/guider/nattarbete/infor-besoket",
          label: { sv: "Pris och förberedelser", en: "Price and preparation" },
        },
      },
    ],
    source: {
      sv: "Arbetsmiljöverket, AFS 2023:15.",
      en: "Swedish Work Environment Authority: AFS 2023:15.",
    },
    relatedCert: "nattarbete",
    bookingHref: "/boka-tid?intyg=nattarbete",
  },

  {
    category: "nattarbete", slug: "infor-besoket",
    label: { sv: "Pris och förberedelser", en: "Prepare your appointment" },
    title: { sv: "Nattarbete – förbered din kontroll", en: "Night work – preparing your medical examination" },
    description: {
      sv: "Pris, direktbokning och underlag att ta med till mottagningen.",
      en: "Price, direct booking and documents to bring to the clinic.",
    },
    sections: [
      {
        id: "bokning",
        title: { sv: "Boka en tid direkt", en: "Arrange your appointment" },
        paragraphs: {
          sv: ["Boka en ledig tid online. Välj rätt medicinsk kontroll och mottagningsbesök i kalendern. Själva besöket görs fysiskt i Göteborg."],
          en: ["Book an available time online. Choose the correct medical examination and clinic visit in the calendar. The appointment takes place in person in Gothenburg."],
        },
        link: {
          href: "/boka-tid?intyg=nattarbete",
          label: { sv: "Boka tid direkt", en: "Book an appointment" },
        },
      },
      {
        id: "pris",
        title: { sv: "Pris och beställning", en: "Price and what is included" },
        paragraphs: {
          sv: ["Pris: 2 000 kr. Stäm av arbetsgivarens beställning före besöket. Lagstadgade medicinska kontroller ska inte medföra kostnader för arbetstagaren."],
          en: ["Price: 2 000 SEK. Confirm the employer's work order before the visit. Statutory medical examinations should not incur costs for the employee."],
        },
      },
      {
        id: "checklista",
        title: { sv: "Förbered dessa uppgifter", en: "Have these ready" },
        paragraphs: {
          sv: ["Ta fram underlagen i god tid. Har du gjort en liknande kontroll tidigare är det bra att ha resultat och datum tillgängliga vid besöket."],
          en: ["Prepare the documents in good time. If you have had a similar examination before, have the results and date available at the visit."],
        },
        items: {
          sv: [
            "Giltig legitimation.",
            "Arbetsgivarens beställning och uppgifter om arbetet.",
            "Schema eller beskrivning av dina nattpass och din återhämtning.",
            "Aktuell läkemedelslista och relevanta tidigare undersökningsresultat.",
          ],
          en: [
            "Valid photo ID.",
            "The employer's work order and details about the work.",
            "Schedule or description of your night shifts and recovery.",
            "Current medication list and relevant previous examination results.",
          ],
        },
      },
      {
        id: "efter",
        title: { sv: "Efter kontrollen", en: "After the assessment" },
        paragraphs: {
          sv: ["Läkaren går igenom bedömningen med dig och berättar om någon uppföljning behövs. Kontrollens återkoppling kan användas i arbetet med att förebygga ohälsa som har samband med arbetstiderna."],
          en: ["The doctor reviews the assessment with you and advises whether any follow-up is needed. The examination feedback can be used in the preventive work related to working hours."],
        },
      },
    ],
    source: {
      sv: "Intygsläkarens besöksupplägg och Arbetsmiljöverkets AFS 2023:15.",
      en: "Intygsläkaren visit information and Swedish Work Environment Authority AFS 2023:15.",
    },
    relatedCert: "nattarbete",
    bookingHref: "/boka-tid?intyg=nattarbete",
  },

  // ── FALLSKÄRM ────────────────────────────────────────────────────────────
  {
    category: "fallskarm", slug: "blankett-och-krav",
    label: { sv: "Blankett och krav", en: "Forms and requirements" },
    title: { sv: "Fallskärmsintyg – blankett och medicinsk bedömning", en: "Parachuting medical – forms and requirements" },
    description: {
      sv: "Vilken blankett behövs och hur förbereder du bedömningen inför din hoppning?",
      en: "Which form do you need and how do you prepare for the medical assessment before your jump?",
    },
    sections: [
      {
        id: "ratt-blankett",
        title: { sv: "Ta med intygsblankett och ifylld hälsodeklaration", en: "Use the current form" },
        paragraphs: {
          sv: [
            "Ta med den aktuella intygsblanketten från fallskärmsklubben eller förbundet. Fyll i hälsodeklarationen före besöket och ta med den.",
            "Fråga klubben vilken blankett och medicinsk bedömning som behövs för din hoppning. Kraven för en elev och andra uppdrag inom fallskärmshoppning kan skilja sig.",
          ],
          en: [
            "Bring the current certificate form from your parachuting club or association. Complete the health declaration before your visit and bring it with you.",
            "Ask your club which form and medical assessment are needed for your jump. Requirements for a student and other roles in parachuting may differ.",
          ],
        },
      },
      {
        id: "ny-elev",
        title: { sv: "Ska du börja en fallskärmsutbildning?", en: "Training, other roles and tandem jumps" },
        paragraphs: {
          sv: [
            "Svenska Fallskärmsförbundet hänvisar till blankett 408:03 för blivande elever. Enligt förbundets information bör läkarundersökningen göras minst fyra veckor före kursstart för att ge tid för eventuella kompletteringar.",
          ],
          en: [
            "Ask your club what requirements apply to your jump or role. A training course, instructor role and tandem jump may have different requirements. For new students, the Swedish Parachute Association recommends a medical examination at least four weeks before the course starts.",
          ],
        },
      },
      {
        id: "bedomning",
        title: { sv: "Vad behöver läkaren bedöma?", en: "Prepare your documents" },
        paragraphs: {
          sv: [
            "Berätta om intyget gäller en ny utbildning eller ett annat uppdrag inom fallskärmshoppning. Läkaren går igenom hälsodeklarationen, undersöker dig och bedömer ditt underlag utifrån den aktuella blanketten.",
            "Berätta om tidigare och aktuella sjukdomar, läkemedel och sådant som kan påverka bedömningen. Läkaren tar ställning utifrån undersökningen och de medicinska uppgifter som finns.",
          ],
          en: [
            "Tell the doctor whether the certificate is for a new training course or another role in parachuting. The doctor reviews the health declaration, examines you and assesses your documentation.",
            "Mention previous and current illnesses, medications and anything that may affect the assessment.",
          ],
        },
      },
      {
        id: "tandem",
        title: { sv: "Gäller det ett tandemhopp?", en: "Examination and price" },
        paragraphs: {
          sv: ["Kontakta arrangören för besked om hälsodeklaration eller läkarintyg behövs för just dig. Ett tandemhopp innebär inte automatiskt att du ska boka samma undersökning som en elev inför en utbildning."],
          en: ["Contact the organiser to find out whether a health declaration or medical certificate is needed for your specific jump. A tandem jump does not automatically require the same examination as a training course student."],
        },
        items: {
          sv: ["Fysiskt besök och undersökning hos legitimerad läkare i Göteborg", "Genomgång av hälsodeklaration och intygsblankett", "Urinsticka", "Blodvärde (Hb)", "Medicinsk bedömning och intyg när underlaget är komplett"],
          en: ["In-person examination by a licensed doctor in Gothenburg", "Review of health declaration and certificate form", "Urine dipstick", "Blood count (Hb)", "Medical certificate when the documentation is complete"],
        },
      },
      {
        id: "forbered",
        title: { sv: "Förbered ditt besök", en: "When is the certificate ready?" },
        paragraphs: {
          sv: ["Ha legitimation, aktuell blankett och relevanta medicinska underlag redo."],
          en: ["The certificate can be completed after the visit when the assessment is done and no supplementary work remains. If further records or examinations are needed, the doctor explains the next step."],
        },
        link: {
          href: "/guider/fallskarm/infor-besoket",
          label: { sv: "Se pris, bokning och förberedelser", en: "Price, booking and preparation" },
        },
      },
    ],
    source: {
      sv: "Faktaunderlag: Svenska Fallskärmsförbundets information om medicinsk tjänstbarhetsbedömning. Kontrollerat 9 september 2026.",
      en: "Source: Swedish Parachute Association information on medical fitness assessment. Checked 9 September 2026.",
    },
    relatedCert: "fallskarmsintyg",
    bookingHref: "/boka-tid?intyg=fallskarmsintyg",
  },

  {
    category: "fallskarm", slug: "infor-besoket",
    label: { sv: "Pris och förberedelser", en: "Prepare your appointment" },
    title: { sv: "Fallskärmsintyg – boka och förbered ditt läkarbesök", en: "Parachuting medical – booking and preparation" },
    description: {
      sv: "Pris, besöksform och en checklista över vad du behöver ha till hands.",
      en: "Price, visit type and a checklist of what to have ready.",
    },
    sections: [
      {
        id: "bokning",
        title: { sv: "Boka din tid direkt online", en: "Arrange your appointment" },
        paragraphs: {
          sv: ["Välj Boka tid för att öppna kalendern för ett fysiskt besök i Göteborg. Välj Fallskärm och en ledig tid i kalendern."],
          en: ["Choose Book appointment to open the calendar for an in-person visit in Gothenburg. Select Parachuting and an available time."],
        },
        link: {
          href: "/boka-tid?intyg=fallskarmsintyg",
          label: { sv: "Boka tid online", en: "Book an appointment" },
        },
      },
      {
        id: "checklista",
        title: { sv: "Ha detta till hands", en: "Have these ready" },
        paragraphs: {
          sv: ["Följ instruktionerna i bokningen för hur underlag ska lämnas. Ta med aktuella handlingar så att läkaren kan bedöma rätt intyg."],
          en: ["Follow the booking instructions on how to submit documents. Bring current documents so the doctor can assess the correct certificate."],
        },
        items: {
          sv: [
            "Ta med den aktuella intygsblanketten från fallskärmsklubben eller förbundet.",
            "Fyll i hälsodeklarationen före besöket och ta med den.",
            "Giltig legitimation.",
            "Klubbens eller förbundets instruktioner.",
            "Aktuell läkemedelslista och relevanta journalunderlag.",
            "Datum för kursstart eller när intyget senast behövs.",
          ],
          en: [
            "Bring the current certificate form from your parachuting club or association.",
            "Complete the health declaration before your visit.",
            "Valid photo ID.",
            "Club or association instructions.",
            "Current medication list and relevant medical records.",
            "Date of course start or when the certificate is needed at the latest.",
          ],
        },
      },
      {
        id: "pris",
        title: { sv: "Pris och innehåll", en: "Price and what is included" },
        paragraphs: {
          sv: ["Priset är 1 800 kr och inkluderar läkarundersökning, urinsticka, blodvärde (Hb) och intyg. Om ditt ärende kräver ytterligare undersökningar får du information om upplägg och eventuell kostnad innan de planeras."],
          en: ["The price is 1 800 SEK and includes the medical examination, urine dipstick, blood count (Hb) and certificate. If your case requires additional examinations, you will be informed of the arrangement and any costs before they are planned."],
        },
        items: {
          sv: ["Fysiskt besök och undersökning hos legitimerad läkare i Göteborg", "Genomgång av hälsodeklaration och intygsblankett", "Urinsticka", "Blodvärde (Hb)", "Medicinsk bedömning och intyg när underlaget är komplett"],
          en: ["In-person examination by a licensed doctor in Gothenburg", "Review of health declaration and certificate form", "Urine dipstick", "Blood count (Hb)", "Medical certificate when documentation is complete"],
        },
      },
      {
        id: "besok",
        title: { sv: "Vid läkarbesöket", en: "During the assessment" },
        paragraphs: {
          sv: ["Berätta om intyget gäller en ny utbildning eller ett annat uppdrag. Läkaren går igenom hälsodeklarationen, undersöker dig och bedömer ditt underlag utifrån den aktuella blanketten."],
          en: ["Tell the doctor whether the certificate is for a new training course or another role. The doctor reviews the health declaration, examines you and assesses your documentation."],
        },
      },
      {
        id: "intyg",
        title: { sv: "När intyget är klart", en: "After the assessment" },
        paragraphs: {
          sv: ["Intyget kan färdigställas efter besöket när bedömningen är klar och inga kompletteringar återstår. Om fler underlag eller undersökningar behövs går läkaren igenom nästa steg med dig."],
          en: ["The certificate can be completed after the visit when the assessment is done and no supplementary work remains. If more records or examinations are needed, the doctor explains the next step."],
        },
      },
    ],
    source: {
      sv: "Praktisk information om Intygsläkarens besök, pris och kompletteringar.",
      en: "Practical information on Intygsläkaren's visit, price and supplementary examinations.",
    },
    relatedCert: "fallskarmsintyg",
    bookingHref: "/boka-tid?intyg=fallskarmsintyg",
  },

  // ── TÄVLINGSINTYG ─────────────────────────────────────────────────────────
  {
    category: "tavlingsintyg", slug: "blankett-och-krav",
    label: { sv: "Blankett och krav", en: "Forms and requirements" },
    title: { sv: "Tävlingsintyg – arrangörens krav, video och undersökningar", en: "Competition medical – forms, video and examinations" },
    description: {
      sv: "Läs om besöksform och eventuella extra kostnader för röntgen, EKG, blodprover eller andra undersökningar.",
      en: "Visit type and any extra costs for X-rays, ECG, blood tests or other examinations.",
    },
    sections: [
      {
        id: "arrangor",
        title: { sv: "Ta fram arrangörens krav", en: "Use the current form" },
        paragraphs: {
          sv: ["Utgå från den aktuella tävlingens instruktioner. Intyget kan behöva innehålla särskilda uppgifter eller skrivas på en bestämd blankett. Skicka eller ta med hela instruktionen enligt bokningens anvisningar."],
          en: ["Base your preparation on the current event's instructions. The certificate may need to contain specific information or be written on a particular form. Bring or send the complete instructions as directed by the booking process."],
        },
        items: {
          sv: [
            "Vilken idrott och tävling gäller intyget?",
            "Vilken blankett och vilket språk efterfrågas?",
            "Vilken giltighet och sista inlämningsdag gäller?",
            "Krävs fysisk undersökning, röntgen, EKG, blodprover, spirometri eller andra underlag?",
          ],
          en: [
            "Which sport and event does the certificate cover?",
            "Which form and language are required?",
            "What validity period and submission deadline apply?",
            "Is a physical examination, X-ray, ECG, blood tests, spirometry or other information required?",
          ],
        },
      },
      {
        id: "video",
        title: { sv: "Välj video när underlaget räcker", en: "Examination and price" },
        paragraphs: {
          sv: ["Video kan passa när läkaren kan bedöma ärendet med dina uppgifter och underlag och mottagaren godtar upplägget. Om intyget kräver en fysisk undersökning, EKG eller spirometri behöver dessa moment göras på mottagning. Kontrollera arrangörens instruktioner före bokningen."],
          en: ["Video works when the doctor can assess the case with your information and the recipient accepts the format. If a physical examination, ECG or spirometry is required, these must be done at the clinic. Check the event instructions before booking."],
        },
      },
      {
        id: "undersokningar",
        title: { sv: "Röntgen, EKG och blodprover vid behov", en: "When is the certificate ready?" },
        paragraphs: {
          sv: [
            "Läkarbedömningen kan behöva kompletteras med röntgen, EKG, blodprover eller spirometri. Dessa moment görs fysiskt. Behovet avgörs utifrån arrangörens krav och läkarens bedömning, inte enbart utifrån vilken idrott du utövar.",
            "Priset är från 1 100 kr för läkarbedömning och intyg. Om röntgen, EKG eller blodprover behövs kan kostnader tillkomma. Du får information om kostnaden innan undersökningarna planeras.",
          ],
          en: [
            "The assessment may need to be supplemented with X-rays, ECG, blood tests or spirometry. These are done in person. The need is determined by the event's requirements and the doctor's assessment – not just the sport.",
            "The price is from 1 100 SEK for the assessment and certificate. If X-rays, ECG or blood tests are needed, costs may be added. You will be informed of costs before examinations are planned.",
          ],
        },
      },
      {
        id: "giltighet",
        title: { sv: "Kontrollera när intyget ska lämnas in", en: "Check the submission deadline" },
        paragraphs: {
          sv: ["Ha tävlingsdatum och sista inlämningsdag redo när du bokar. Kontrollera hur nyligen intyget måste vara utfärdat. Har du ett tidigare intyg behöver arrangören bekräfta att det fortfarande kan användas."],
          en: ["Have the event date and submission deadline ready when booking. Check how recently the certificate must have been issued. If you have a previous certificate, the event organiser needs to confirm it can still be used."],
        },
      },
      {
        id: "forbered",
        title: { sv: "Förbered ditt besök", en: "Your documents" },
        paragraphs: {
          sv: ["Ha legitimation, aktuell blankett och relevanta medicinska underlag redo."],
          en: ["Have photo ID, the current form and relevant medical records ready."],
        },
        link: {
          href: "/guider/tavlingsintyg/infor-besoket",
          label: { sv: "Se pris, bokning och förberedelser", en: "Price, booking and preparation" },
        },
      },
    ],
    source: {
      sv: "Praktisk information om Intygsläkarens tävlingsintyg. Arrangörens aktuella instruktioner styr vilket intyg som behövs.",
      en: "Practical information on Intygsläkaren's competition medical. The event's current instructions determine which certificate is required.",
    },
    relatedCert: "tavlingsintyg",
    bookingHref: "/boka-tid?intyg=tavlingsintyg",
  },

  {
    category: "tavlingsintyg", slug: "infor-besoket",
    label: { sv: "Pris och förberedelser", en: "Prepare your appointment" },
    title: { sv: "Tävlingsintyg – boka och förbered ditt läkarbesök", en: "Competition medical – booking and preparation" },
    description: {
      sv: "Pris, besöksform och en checklista över vad du behöver ha till hands.",
      en: "Price, visit type and a checklist of what to have ready.",
    },
    sections: [
      {
        id: "bokning",
        title: { sv: "Boka din tid direkt online", en: "Arrange your appointment" },
        paragraphs: {
          sv: ["Välj video eller mottagningsbesök utifrån intygets krav. När du väljer besöksform öppnas kalendern. Välj tävlingsintyg, rätt besöksform och en ledig tid i kalendern."],
          en: ["Choose video or clinic visit depending on the certificate's requirements. The calendar opens when you select a visit type. Choose Competition medical, the right visit type and an available time."],
        },
        link: {
          href: "/boka-tid?intyg=tavlingsintyg",
          label: { sv: "Boka tid online", en: "Book an appointment" },
        },
      },
      {
        id: "checklista",
        title: { sv: "Ha detta till hands", en: "Have these ready" },
        paragraphs: {
          sv: ["Följ instruktionerna i bokningen för hur underlag ska lämnas. Ta med aktuella handlingar så att läkaren kan bedöma rätt intyg."],
          en: ["Follow the booking instructions. Bring current documents so the doctor can assess the correct certificate."],
        },
        items: {
          sv: [
            "Giltig legitimation.",
            "Arrangörens eller förbundets aktuella intygsblankett.",
            "Uppgifter om idrott, tävling, datum och sista inlämningsdag.",
            "Instruktioner om språk, giltighet och eventuella undersökningar.",
            "Läkemedelslista och relevanta medicinska underlag.",
          ],
          en: [
            "Valid photo ID.",
            "The event organiser's or association's current certificate form.",
            "Details of sport, event, date and submission deadline.",
            "Instructions about language, validity and any required examinations.",
            "Medication list and relevant medical records.",
          ],
        },
      },
      {
        id: "pris",
        title: { sv: "Pris och innehåll", en: "Price and what is included" },
        paragraphs: {
          sv: ["Priset är från 1 100 kr för läkarbedömning och intyg. Om röntgen, EKG eller blodprover behövs kan kostnader tillkomma. Även spirometri och andra kompletterande undersökningar kan medföra extra kostnader. Du får information om kostnaden innan undersökningarna planeras."],
          en: ["The price is from 1 100 SEK for the assessment and certificate. If X-rays, ECG or blood tests are needed, costs may be added. Spirometry and other supplementary examinations may also incur extra costs."],
        },
        items: {
          sv: ["Läkarbesök och individuell medicinsk bedömning", "Genomgång av arrangörens intygskrav", "Intyg när det finns tillräckligt medicinskt underlag"],
          en: ["Medical appointment and individual assessment", "Review of the event's certificate requirements", "Certificate when sufficient medical documentation is available"],
        },
      },
      {
        id: "besok",
        title: { sv: "Vid läkarbesöket", en: "During the assessment" },
        paragraphs: {
          sv: ["Ta fram arrangörens aktuella medicinska blankett och instruktioner. Berätta vilken idrott och tävling intyget gäller. Läkaren går igenom din hälsa och avgör vilka undersökningar och underlag som behövs."],
          en: ["Bring the current medical form and instructions from the event organiser. Tell the doctor which sport and event the certificate covers. The doctor reviews your health and decides which examinations are needed."],
        },
      },
      {
        id: "intyg",
        title: { sv: "När intyget är klart", en: "After the assessment" },
        paragraphs: {
          sv: ["Intyget färdigställs när läkaren har genomfört bedömningen och alla uppgifter som arrangören kräver finns. Boka i god tid före tävlingen om undersökningar eller provsvar behöver kompletteras."],
          en: ["The certificate is completed when the doctor has finished the assessment and all information required by the event organiser is available. Book well before the event if examinations or results need to be supplemented."],
        },
      },
    ],
    source: {
      sv: "Praktisk information om Intygsläkarens besök, pris och kompletteringar.",
      en: "Practical information on Intygsläkaren's visit, price and supplementary examinations.",
    },
    relatedCert: "tavlingsintyg",
    bookingHref: "/boka-tid?intyg=tavlingsintyg",
  },

  // ── ADOPTIONSINTYG ────────────────────────────────────────────────────────
  {
    category: "adoptionsintyg", slug: "blankett-och-krav",
    label: { sv: "Blankett och krav", en: "Forms and requirements" },
    title: { sv: "Adoptionsintyg – hälsodeklaration och rätt blankett", en: "Adoption medical – health declaration and forms" },
    description: {
      sv: "Förbered de medicinska handlingarna inför läkarbesöket och din adoptionsutredning.",
      en: "Prepare the medical documents for your appointment and adoption assessment.",
    },
    sections: [
      {
        id: "handlingar",
        title: { sv: "Hälsodeklaration och läkarintyg", en: "Use the current form" },
        paragraphs: {
          sv: [
            "MFoF tillhandahåller blankett 4608 för hälsodeklaration och 4609 för läkarintyg vid internationell adoption. Följ familjerättens eller adoptionsorganisationens instruktioner och ta med de handlingar som har begärts.",
            "Fyll i hälsodeklarationen före besöket. Läkaren går igenom dina uppgifter tillsammans med dig och genomför läkarundersökningen.",
          ],
          en: [
            "MFoF provides forms for the health declaration and medical certificate for international adoption. Follow the instructions from the family law office or adoption organisation and bring the documents that have been requested.",
            "Complete the health declaration before your visit. The doctor reviews your information with you and carries out the medical examination.",
          ],
        },
      },
      {
        id: "mottagare",
        title: { sv: "Ta med eventuella ytterligare krav", en: "Health declaration and adoption documents" },
        paragraphs: {
          sv: ["Om organisationen eller mottagarlandet efterfrågar en särskild blankett, ett visst språk eller ytterligare provsvar behöver läkaren få de instruktionerna. Det hjälper oss att planera rätt undersökningar från början."],
          en: ["Use the instructions from the family law office or adoption organisation. The receiving country or organisation may ask for additional documents. Bring all requirements to the appointment."],
        },
      },
      {
        id: "underlag",
        title: { sv: "Om medicinska underlag behöver kompletteras", en: "Prepare your documents" },
        paragraphs: {
          sv: ["Tidigare och aktuella sjukdomar och behandlingar behöver framgå av underlaget. Vid behov kan läkaren efterfråga journalhandlingar eller ett utlåtande från en behandlande specialist. Ta med relevanta handlingar om du redan har dem."],
          en: [
            "Previous and current illnesses and treatments must be reflected in the documentation. If needed, the doctor may request medical records or a report from a treating specialist.",
          ],
        },
        items: {
          sv: [
            "Giltig legitimation och ifylld hälsodeklaration.",
            "Aktuell läkarintygsblankett.",
            "Instruktioner från familjerätten, adoptionsorganisationen eller mottagarlandet.",
            "Aktuell läkemedelslista och relevanta journalhandlingar.",
            "Uppgifter om språk, sista inlämningsdag och eventuella särskilda provkrav.",
          ],
          en: [
            "Valid photo ID and completed health declaration.",
            "Current medical certificate form.",
            "Instructions from family law office, adoption organisation or receiving country.",
            "Current medication list and relevant medical records.",
            "Required language, deadline and any specific test requirements.",
          ],
        },
      },
      {
        id: "efter",
        title: { sv: "Intyget som underlag i processen", en: "When is the certificate ready?" },
        paragraphs: {
          sv: [
            "Intyget utfärdas när nödvändiga provsvar har kommit och den medicinska bedömningen är klar. Om kompletterande journalunderlag eller ett specialistutlåtande behövs får du veta vad som återstår.",
            "Läkarintyget ingår i underlaget för den fortsatta adoptionsutredningen. Följ mottagarens instruktioner för inlämning av intyg och övriga handlingar.",
          ],
          en: [
            "The certificate is issued when the necessary test results have arrived and the medical assessment is complete. If supplementary medical records or a specialist report are needed, you will be told what remains.",
            "The medical certificate forms part of the documentation for the continuing adoption assessment. Follow the recipient's instructions for submitting the certificate and other documents.",
          ],
        },
      },
      {
        id: "forbered",
        title: { sv: "Förbered ditt besök", en: "Prepare your visit" },
        paragraphs: {
          sv: ["Ha legitimation, aktuell blankett och relevanta medicinska underlag redo."],
          en: ["Have your ID, current form and relevant medical records ready."],
        },
        link: {
          href: "/guider/adoptionsintyg/infor-besoket",
          label: { sv: "Se pris, bokning och förberedelser", en: "Price, booking and preparation" },
        },
      },
    ],
    source: {
      sv: "Faktaunderlag: MFoF:s blanketter och information om hälsodeklaration inför internationell adoption. Kontrollerat 9 september 2026.",
      en: "Source: MFoF forms and information on health declarations for international adoption. Checked 9 September 2026.",
    },
    relatedCert: "adoptionsintyg",
    bookingHref: "/boka-tid?intyg=adoptionsintyg",
  },

  {
    category: "adoptionsintyg", slug: "infor-besoket",
    label: { sv: "Pris och förberedelser", en: "Prepare your appointment" },
    title: { sv: "Adoptionsintyg – boka och förbered ditt läkarbesök", en: "Adoption medical – booking and preparation" },
    description: {
      sv: "Pris, besöksform och en checklista över vad du behöver ha till hands.",
      en: "Price, visit type and a checklist of what to have ready.",
    },
    sections: [
      {
        id: "bokning",
        title: { sv: "Boka din tid direkt online", en: "Arrange your appointment" },
        paragraphs: {
          sv: ["Välj Boka tid för att öppna kalendern för ett fysiskt besök i Göteborg. Välj Adoptionsintyg och en ledig tid."],
          en: ["Choose Book appointment for an in-person visit in Gothenburg. Select Adoption medical and an available time."],
        },
        link: {
          href: "/boka-tid?intyg=adoptionsintyg",
          label: { sv: "Boka tid online", en: "Book an appointment" },
        },
      },
      {
        id: "checklista",
        title: { sv: "Ha detta till hands", en: "Have these ready" },
        paragraphs: {
          sv: ["Följ instruktionerna i bokningen för hur underlag ska lämnas. Ta med aktuella handlingar så att läkaren kan bedöma rätt intyg."],
          en: ["Follow the booking instructions. Bring current documents so the doctor can assess the correct certificate."],
        },
        items: {
          sv: [
            "Giltig legitimation.",
            "Ifylld hälsodeklaration och aktuell läkarintygsblankett.",
            "Instruktioner från familjerätten, adoptionsorganisationen eller mottagarlandet.",
            "Aktuell läkemedelslista och relevanta journalhandlingar.",
            "Uppgifter om språk, sista inlämningsdag och eventuella särskilda provkrav.",
          ],
          en: [
            "Valid photo ID.",
            "Completed health declaration and current medical certificate form.",
            "Instructions from family law office, adoption organisation or receiving country.",
            "Current medication list and relevant medical records.",
            "Required language, deadline and any specific test requirements.",
          ],
        },
      },
      {
        id: "pris",
        title: { sv: "Pris och innehåll", en: "Price and what is included" },
        paragraphs: {
          sv: ["Priset är 2 600 kr inklusive läkarundersökning, provtagning för adoptionsintyget och intyg."],
          en: ["The price is 2 600 SEK including the medical examination, sampling for the adoption certificate and the certificate itself."],
        },
        items: {
          sv: ["Fysiskt besök och undersökning hos legitimerad läkare i Göteborg", "Genomgång av hälsodeklaration och intygsblankett", "Provtagning för adoptionsintyget", "Medicinsk bedömning och intyg när nödvändiga provsvar finns"],
          en: ["In-person examination by a licensed doctor in Gothenburg", "Review of health declaration and certificate form", "Sampling for the adoption certificate", "Medical certificate when required test results are available"],
        },
      },
      {
        id: "besok",
        title: { sv: "Vid läkarbesöket", en: "During the assessment" },
        paragraphs: {
          sv: ["Läkaren går igenom din hälsodeklaration, tidigare och aktuella sjukdomar samt läkemedel. Undersökningen och eventuella underlag anpassas efter den blankett du har fått. Intyget är ett medicinskt underlag i adoptionsprocessen; beslut om medgivande fattas i den fortsatta handläggningen."],
          en: ["The doctor reviews your health declaration, previous and current illnesses and medication. The examination is adapted to the form you have received. The certificate is medical evidence in the adoption process; the decision on approval is made in the continuing process."],
        },
      },
      {
        id: "intyg",
        title: { sv: "När intyget är klart", en: "After the assessment" },
        paragraphs: {
          sv: ["Intyget utfärdas när nödvändiga provsvar har kommit och den medicinska bedömningen är klar. Om kompletterande journalunderlag eller ett specialistutlåtande behövs får du veta vad som återstår."],
          en: ["The certificate is issued when the necessary test results have arrived and the medical assessment is complete. If supplementary records or a specialist report are needed, you will be told what remains."],
        },
      },
    ],
    source: {
      sv: "Praktisk information om Intygsläkarens besök, pris och kompletteringar.",
      en: "Practical information on Intygsläkaren's visit, price and supplementary examinations.",
    },
    relatedCert: "adoptionsintyg",
    bookingHref: "/boka-tid?intyg=adoptionsintyg",
  },

  // ── AFS HANDINTENSIVT ─────────────────────────────────────────────────────
  {
    category: "afs-handintensivt", slug: "regler-och-kontroll",
    label: { sv: "Regler och intervall", en: "Requirements and examination" },
    title: { sv: "Handintensivt arbete – krav och medicinsk kontroll", en: "Hand-intensive work – medical examination requirements" },
    description: { sv: "När kontrollen behövs, vad som undersöks och hur uppföljningen planeras.", en: "When the examination is required, what is examined and how follow-up is planned." },
    sections: [
      { id: "nar", title: { sv: "När behövs kontrollen?", en: "When is the examination required?" }, paragraphs: { sv: ["Kontrollen ska anordnas när riskbedömningen visar kvarstående ökad risk för belastningsbesvär trots arbetsmiljöåtgärder. Det är kombinationen av ihållande snabba handledsrörelser och kraft som avses med handintensivt arbete."], en: ["The examination is required when the risk assessment shows a remaining increased risk of musculoskeletal disorders despite workplace measures. Hand-intensive work involves sustained rapid wrist movements combined with force."] } },
      { id: "intervall", title: { sv: "När ska den göras och upprepas?", en: "Timing and repeat examinations" }, paragraphs: { sv: ["Första kontrollen ska göras inom tre år från arbetets början och därefter med högst tre års mellanrum. Vid nya arbetsrelaterade besvär ska kontrollen ordnas inom en månad från att arbetsgivaren fått kännedom om besvären."], en: ["The first examination is within three years of starting the work, then at intervals of no more than three years. New work-related symptoms require an examination within one month of the employer becoming aware."] } },
      { id: "undersokning", title: { sv: "Vad görs vid kontrollen?", en: "What is examined?" }, paragraphs: { sv: ["Vi går igenom arbetsmoment, belastning och eventuella besvär i händer, armar, skuldror och nacke. Ta med arbetsgivarens underlag så att bedömningen kan kopplas till ditt arbete."], en: ["We review your work tasks, physical load and symptoms in your hands, arms, shoulders and neck. Bring the employer's documentation so the assessment can be linked to your work."] }, items: { sv: ["Genomgång av arbetsuppgifter, belastning och besvär", "Bedömning av behovet av klinisk undersökning av händer, armar, skuldror och nacke", "Återkoppling och råd om fortsatt uppföljning"], en: ["Review of tasks, physical load and symptoms", "Assessment of the need for clinical examination of hands, arms, shoulders and neck", "Feedback and advice on follow-up"] } },
      { id: "intyg", title: { sv: "Kontroll och tjänstbarhetsintyg", en: "Examination and fitness certificate" }, paragraphs: { sv: ["För handintensivt arbete krävs inget tjänstbarhetsintyg enligt AFS 2023:15. Du får återkoppling på den medicinska bedömningen."], en: ["A certificate of fitness for work is not required for hand-intensive work under AFS 2023:15. You receive feedback on the medical assessment."] } },
      { id: "arbetsgivare", title: { sv: "Arbetsgivarens underlag behövs", en: "Information from the employer" }, paragraphs: { sv: ["Arbetsgivarens riskbedömning visar vilka arbetsmoment och exponeringar som behöver bedömas. Ha beställningen tillgänglig och följ checklistan inför besöket."], en: ["The employer's risk assessment shows which tasks and exposures need to be assessed. Have the work order available and follow the checklist before the visit."] }, link: { href: "/guider/afs-handintensivt/infor-besoket", label: { sv: "Pris och förberedelser", en: "Price and preparation" } } },
    ],
    source: { sv: "Arbetsmiljöverket, AFS 2023:15, 3 kap. 4–6 §§ och bilaga 2.", en: "Swedish Work Environment Authority: AFS 2023:15, Chapter 3, sections 4–6 and Annex 2." },
    relatedCert: "afs-handintensivt",
    bookingHref: "/boka-tid?intyg=afs-handintensivt",
  },

  {
    category: "afs-handintensivt", slug: "infor-besoket",
    label: { sv: "Pris och förberedelser", en: "Prepare your appointment" },
    title: { sv: "Handintensivt arbete – förbered din kontroll", en: "Hand-intensive work – preparing your medical examination" },
    description: { sv: "Pris, direktbokning och underlag att ta med till mottagningen.", en: "Price, direct booking and documents to bring to the clinic." },
    sections: [
      { id: "bokning", title: { sv: "Boka en tid direkt", en: "Arrange your appointment" }, paragraphs: { sv: ["Boka en ledig tid online. Välj rätt medicinsk kontroll och mottagningsbesök i kalendern. Besöket görs fysiskt i Göteborg."], en: ["Book an available time online. Choose the correct medical examination and clinic visit in the calendar. The appointment takes place in person in Gothenburg."] }, link: { href: "/boka-tid?intyg=afs-handintensivt", label: { sv: "Boka tid direkt", en: "Book an appointment" } } },
      { id: "pris", title: { sv: "Pris och beställning", en: "Price and what is included" }, paragraphs: { sv: ["Pris: 2 000 kr. Stäm av arbetsgivarens beställning före besöket. Lagstadgade medicinska kontroller ska inte medföra kostnader för arbetstagaren."], en: ["Price: 2 000 SEK. Confirm the employer's work order before the visit. Statutory medical examinations should not incur costs for the employee."] } },
      { id: "checklista", title: { sv: "Förbered dessa uppgifter", en: "Have these ready" }, paragraphs: { sv: ["Ta fram underlagen i god tid."], en: ["Prepare the documents in good time."] }, items: { sv: [...commonWorkEnvPrep.sv, "Beskriv hur länge och hur ofta du utför de handintensiva momenten."], en: [...commonWorkEnvPrep.en, "How long and how often you perform the hand-intensive tasks."] } },
      { id: "efter", title: { sv: "Efter kontrollen", en: "After the assessment" }, paragraphs: { sv: ["För handintensivt arbete krävs inget tjänstbarhetsintyg enligt AFS 2023:15. Du får återkoppling på den medicinska bedömningen. Du får besked om eventuella kompletteringar och fortsatt uppföljning."], en: ["A certificate of fitness for work is not required. You receive feedback on the medical assessment and advice on any follow-up."] } },
    ],
    source: { sv: "Intygsläkarens besöksupplägg och Arbetsmiljöverkets AFS 2023:15.", en: "Intygsläkaren visit information and Swedish Work Environment Authority AFS 2023:15." },
    relatedCert: "afs-handintensivt",
    bookingHref: "/boka-tid?intyg=afs-handintensivt",
  },

  // ── AFS VIBRATIONER ───────────────────────────────────────────────────────
  {
    category: "afs-vibrationer", slug: "regler-och-kontroll",
    label: { sv: "Regler och intervall", en: "Requirements and examination" },
    title: { sv: "Vibrationer – krav och medicinsk kontroll", en: "Vibration exposure – medical examination requirements" },
    description: { sv: "När kontrollen behövs, vad som undersöks och hur uppföljningen planeras.", en: "When the examination is required, what is examined and how follow-up is planned." },
    sections: [
      { id: "nar", title: { sv: "När behövs kontrollen?", en: "When is the examination required?" }, paragraphs: { sv: ["Medicinsk kontroll behövs bland annat när insatsvärdet för hand- och armvibrationer överskrids eller exponeringen misstänks kunna orsaka ohälsa. Även skador hos en kollega med liknande exponering kan utlösa krav på kontroll."], en: ["Examinations are required, for example, when the action value for hand-arm vibration is exceeded or exposure is suspected of causing ill health. An injury in a colleague with similar exposure can also trigger the requirement."] } },
      { id: "intervall", title: { sv: "När ska den göras och upprepas?", en: "Timing and repeat examinations" }, paragraphs: { sv: ["Kontroll görs före arbetets början och återkommande med högst tre års mellanrum. Vid tecken på ny eller förvärrad vibrationsskada ska kontroll ordnas inom en månad efter arbetsgivarens kännedom. En kontroll under de senaste tolv månaderna kan ersätta kontrollen före start."], en: ["Before starting the work and at intervals of no more than three years. New or worsening vibration injury requires examination within one month of the employer becoming aware. An examination within the previous twelve months may replace the pre-start examination."] } },
      { id: "undersokning", title: { sv: "Vad görs vid kontrollen?", en: "What is examined?" }, paragraphs: { sv: ["Läkaren går igenom vilka verktyg du använder, exponeringstiden och eventuella symtom. Berätta vid besöket om till exempel domningar, nedsatt känsel eller fingrar som blir vita i kyla."], en: ["The doctor reviews the tools used, exposure time and symptoms, such as numbness, reduced sensation or fingers turning white in cold conditions."] }, items: { sv: ["Genomgång av verktyg, exponering och tidigare arbete", "Läkarundersökning med fokus på kärl, nerver och funktion i händer och armar", "Bedömning av symtom och råd om fortsatt uppföljning"], en: ["Review of tools, exposure and previous work", "Examination focusing on circulation, nerves and function in the hands and arms", "Assessment of symptoms and advice on follow-up"] } },
      { id: "intyg", title: { sv: "Kontroll och tjänstbarhetsintyg", en: "Examination and fitness certificate" }, paragraphs: { sv: ["Vibrationskontrollen innebär medicinsk bedömning och återkoppling. Den omfattas inte av krav på tjänstbarhetsintyg enligt AFS 2023:15."], en: ["Vibration examinations involve medical assessment and feedback. A certificate of fitness for work is not required under AFS 2023:15."] } },
      { id: "arbetsgivare", title: { sv: "Arbetsgivarens underlag behövs", en: "Information from the employer" }, paragraphs: { sv: ["Arbetsgivarens riskbedömning visar vilka arbetsmoment och exponeringar som behöver bedömas."], en: ["The employer's risk assessment shows which tasks and exposures need to be assessed."] }, link: { href: "/guider/afs-vibrationer/infor-besoket", label: { sv: "Pris och förberedelser", en: "Price and preparation" } } },
    ],
    source: { sv: "Arbetsmiljöverket, AFS 2023:15, 3 kap. 1–3 §§ och bilaga 1.", en: "Swedish Work Environment Authority: AFS 2023:15, Chapter 3, sections 1–3 and Annex 1." },
    relatedCert: "afs-vibrationer",
    bookingHref: "/boka-tid?intyg=afs-vibrationer",
  },

  {
    category: "afs-vibrationer", slug: "infor-besoket",
    label: { sv: "Pris och förberedelser", en: "Prepare your appointment" },
    title: { sv: "Vibrationer – förbered din kontroll", en: "Vibration exposure – preparing your medical examination" },
    description: { sv: "Pris, direktbokning och underlag att ta med till mottagningen.", en: "Price, direct booking and documents to bring to the clinic." },
    sections: [
      { id: "bokning", title: { sv: "Boka en tid direkt", en: "Arrange your appointment" }, paragraphs: { sv: ["Boka en ledig tid online. Välj rätt medicinsk kontroll och mottagningsbesök i kalendern. Besöket görs fysiskt i Göteborg."], en: ["Book an available time online. The appointment takes place in person in Gothenburg."] }, link: { href: "/boka-tid?intyg=afs-vibrationer", label: { sv: "Boka tid direkt", en: "Book an appointment" } } },
      { id: "pris", title: { sv: "Pris och beställning", en: "Price and what is included" }, paragraphs: { sv: ["Pris: 2 000 kr. Stäm av arbetsgivarens beställning. Lagstadgade medicinska kontroller ska inte medföra kostnader för arbetstagaren."], en: ["Price: 2 000 SEK. Statutory medical examinations should not incur costs for the employee."] } },
      { id: "checklista", title: { sv: "Förbered dessa uppgifter", en: "Have these ready" }, paragraphs: { sv: ["Ta fram underlagen i god tid."], en: ["Prepare the documents in good time."] }, items: { sv: [...commonWorkEnvPrep.sv, "Lista över verktyg, dagliga användningstider och eventuell beräkning av vibrationsexponeringen."], en: [...commonWorkEnvPrep.en, "A list of tools, daily use times and any calculated vibration exposure."] } },
      { id: "efter", title: { sv: "Efter kontrollen", en: "After the assessment" }, paragraphs: { sv: ["Vibrationskontrollen innebär medicinsk bedömning och återkoppling. Du får besked om eventuella kompletteringar och fortsatt uppföljning."], en: ["The vibration examination involves medical assessment and feedback. You will be advised of any follow-up needed."] } },
    ],
    source: { sv: "Intygsläkarens besöksupplägg och Arbetsmiljöverkets AFS 2023:15.", en: "Intygsläkaren visit information and Swedish Work Environment Authority AFS 2023:15." },
    relatedCert: "afs-vibrationer",
    bookingHref: "/boka-tid?intyg=afs-vibrationer",
  },

  // ── AFS ALLERGIFRAMKALLANDE ────────────────────────────────────────────────
  {
    category: "afs-allergiframkallande", slug: "regler-och-kontroll",
    label: { sv: "Regler och intervall", en: "Requirements and examination" },
    title: { sv: "Allergiframkallande ämnen – krav och medicinsk kontroll", en: "Sensitising chemicals – medical examination requirements" },
    description: { sv: "När kontrollen behövs, vad som undersöks och hur uppföljningen planeras.", en: "When the examination is required, what is examined and how follow-up is planned." },
    sections: [
      { id: "nar", title: { sv: "När behövs kontrollen?", en: "When is the examination required?" }, paragraphs: { sv: ["För vissa akrylater, epoxiplastkomponenter och formaldehydhartser krävs medicinsk kontroll. För bland annat vissa diisocyanater, organiska syraanhydrider och cyanoakrylater kan även tjänstbarhetsintyg krävas. Produktklassificering, arbetssätt och exponering avgör vilka regler som gäller."], en: ["Some acrylates, epoxy components and formaldehyde resins require medical examinations. Certain diisocyanates, organic acid anhydrides and cyanoacrylates may also require a certificate of fitness for work. Classification and exposure determine which rules apply."] } },
      { id: "intervall", title: { sv: "När ska den göras och upprepas?", en: "Timing and repeat examinations" }, paragraphs: { sv: ["Kontroll planeras normalt före arbetets början. När 5 kap. gäller ska uppföljning även göras efter tre till sex månader och sedan med högst två års mellanrum. För kontroller enligt 4 kap. styr bland annat nya hud- eller luftvägsbesvär behovet av ny kontroll."], en: ["Normally planned before the work starts. Where Chapter 5 applies, follow-up is also required after three to six months and then at most every two years. Under Chapter 4, new skin or respiratory symptoms can determine the need for another examination."] } },
      { id: "undersokning", title: { sv: "Vad görs vid kontrollen?", en: "What is examined?" }, paragraphs: { sv: ["Börja med produktnamn och en kort beskrivning av arbetet. Vi återkommer om vilka säkerhetsdatablad och riskbedömningar som behövs, hur kontrollen ska läggas upp och när den kan genomföras."], en: ["Start with product names and a description of the work. We advise which safety data sheets and risk assessments are needed and confirm the examination plan and timing."] }, items: { sv: ["Genomgång av kemiska produkter, exponering och hälsa", "Undersökning av hud och luftvägar samt lungfunktion", "Bedömning och plan för eventuell återkommande kontroll"], en: ["Review of chemical products, exposure and health", "Assessment of the skin, airways and lung function", "Assessment and plan for any recurring examinations"] } },
      { id: "intyg", title: { sv: "Kontroll och tjänstbarhetsintyg", en: "Examination and fitness certificate" }, paragraphs: { sv: ["Vi går igenom om kontrollen ska omfatta bedömning för tjänstbarhetsintyg. Ett sådant intyg kan utfärdas först när den medicinska bedömningen och kraven för arbetet är uppfyllda."], en: ["We review whether the examination needs to include assessment for a certificate of fitness for work. A certificate can be issued only after the medical assessment and work requirements are met."] } },
      { id: "arbetsgivare", title: { sv: "Arbetsgivarens underlag behövs", en: "Information from the employer" }, paragraphs: { sv: ["Arbetsgivarens riskbedömning visar vilka arbetsmoment och exponeringar som behöver bedömas."], en: ["The employer's risk assessment shows which tasks and exposures need to be assessed."] }, link: { href: "/guider/afs-allergiframkallande/infor-besoket", label: { sv: "Pris och förberedelser", en: "Price and preparation" } } },
    ],
    source: { sv: "Arbetsmiljöverket, AFS 2023:15, 4–5 kap. och bilagorna 4–5.", en: "Swedish Work Environment Authority: AFS 2023:15, Chapters 4–5 and Annexes 4–5." },
  },

  {
    category: "afs-allergiframkallande", slug: "infor-besoket",
    label: { sv: "Pris och förberedelser", en: "Prepare your appointment" },
    title: { sv: "Allergiframkallande ämnen – förbered din kontroll", en: "Sensitising chemicals – preparing your medical examination" },
    description: { sv: "Det här behöver ni förbereda när vi planerar kontrollen tillsammans.", en: "What you need to prepare when we plan the examination together." },
    sections: [
      { id: "bokning", title: { sv: "Börja med en förfrågan", en: "Send an enquiry" }, paragraphs: { sv: ["Lämna kontaktuppgifter, företag, antal personer och en kort beskrivning av arbetet i formuläret. Vi återkommer för att planera underlag, undersökningar och tider."], en: ["Provide contact details, company name, number of people and a brief description of the work. We will contact you to plan the documentation, examinations and timing."] }, link: { href: "/lakarintyg/afs-allergiframkallande-kemiska-arbeten#planera-kontroll", label: { sv: "Skicka förfrågan", en: "Send an enquiry" } } },
      { id: "pris", title: { sv: "Pris och beställning", en: "Price and what is included" }, paragraphs: { sv: ["Pris: Från 3 000 kr. Vi bekräftar omfattning och kostnad innan bokningen. Eventuella kompletterande undersökningar och återkommande prover behöver planeras utifrån arbetet."], en: ["Price: from 3 000 SEK. We confirm the scope and cost before booking. Any supplementary examinations and recurring tests need to be planned based on the work."] } },
      { id: "checklista", title: { sv: "Förbered dessa uppgifter", en: "Have these ready" }, paragraphs: { sv: ["Ta fram underlagen i god tid."], en: ["Prepare the documents in good time."] }, items: { sv: [...commonWorkEnvPrep.sv, "Produktnamn, aktuella säkerhetsdatablad samt exponeringstid och arbetssätt."], en: [...commonWorkEnvPrep.en, "Product names, current safety data sheets, exposure times and working methods."] } },
      { id: "efter", title: { sv: "Efter kontrollen", en: "After the assessment" }, paragraphs: { sv: ["Vi går igenom om kontrollen ska omfatta bedömning för tjänstbarhetsintyg. Du får besked om eventuella kompletteringar och fortsatt uppföljning."], en: ["We review whether a fitness certificate is required. You will be advised of any further steps and follow-up."] } },
    ],
    source: { sv: "Intygsläkarens besöksupplägg och Arbetsmiljöverkets AFS 2023:15.", en: "Intygsläkaren visit information and Swedish Work Environment Authority AFS 2023:15." },
  },

  // ── AFS ASBEST-KVARTS ─────────────────────────────────────────────────────
  {
    category: "afs-asbest-kvarts", slug: "regler-och-kontroll",
    label: { sv: "Regler och intervall", en: "Requirements and examination" },
    title: { sv: "Asbest, kvarts och fibrer – krav och medicinsk kontroll", en: "Asbestos, silica and fibres – medical examination requirements" },
    description: { sv: "När kontrollen behövs, vad som undersöks och hur uppföljningen planeras.", en: "When the examination is required, what is examined and how follow-up is planned." },
    sections: [
      { id: "nar", title: { sv: "När behövs kontrollen?", en: "When is the examination required?" }, paragraphs: { sv: ["Reglerna omfattar asbestexponerat arbete och, vid angivna exponeringsnivåer eller tider, vissa syntetiska oorganiska fibrer och respirabel kvarts eller kristobalit. Asbestkravet omfattar även annat arbete som medför exponering för asbesthaltigt damm, inte bara tillståndspliktig sanering."], en: ["The rules cover asbestos exposure and, at specified levels or durations, certain synthetic inorganic fibres and respirable quartz or cristobalite. Asbestos requirements extend beyond licensed removal work to other work involving asbestos-containing dust."] } },
      { id: "intervall", title: { sv: "När ska den göras och upprepas?", en: "Timing and repeat examinations" }, paragraphs: { sv: ["När arbetet omfattas av reglerna ska kontroll med bedömning för tjänstbarhetsintyg göras före start och sedan med högst tre års mellanrum, eller tidigare enligt intyget. Ett redan giltigt intyg kan gälla vid motsvarande arbete."], en: ["For work covered by the rules, examination and assessment for a fitness certificate are required before starting and at most every three years, or sooner if stated on the certificate. An existing valid certificate may cover equivalent work."] } },
      { id: "undersokning", title: { sv: "Vad görs vid kontrollen?", en: "What is examined?" }, paragraphs: { sv: ["Ange vilket damm arbetet gäller och hur många personer som behöver kontroll. Vi går igenom vilka exponeringsuppgifter, tidigare kontroller och undersökningsresultat som behövs."], en: ["State which dust the work involves and how many people need examination. We review the exposure history, previous checks and any lung examination results needed."] }, items: { sv: ["Genomgång av dammexponering, tidigare arbete och hälsa", "Läkarundersökning och bedömning av lungfunktion", "Planering av eventuella kompletterande undersökningar"], en: ["Review of dust exposure, work history and health", "Medical examination and assessment of lung function", "Planning of any additional examinations"] } },
      { id: "intyg", title: { sv: "Kontroll och tjänstbarhetsintyg", en: "Examination and fitness certificate" }, paragraphs: { sv: ["De arbeten som omfattas av 6 kap. kräver tjänstbarhetsintyg. Intyget utfärdas efter medicinsk bedömning när nödvändiga underlag finns och kraven är uppfyllda."], en: ["Work covered by Chapter 6 requires a certificate of fitness for work. It is issued after the medical assessment when the necessary records are available and the requirements are met."] } },
      { id: "arbetsgivare", title: { sv: "Arbetsgivarens underlag behövs", en: "Information from the employer" }, paragraphs: { sv: ["Arbetsgivarens riskbedömning visar vilka arbetsmoment och exponeringar som behöver bedömas."], en: ["The employer's risk assessment shows which tasks and exposures need to be assessed."] }, link: { href: "/guider/afs-asbest-kvarts/infor-besoket", label: { sv: "Pris och förberedelser", en: "Price and preparation" } } },
    ],
    source: { sv: "Arbetsmiljöverket, AFS 2023:15, 6 kap. och bilaga 6; ändring AFS 2025:7.", en: "Swedish Work Environment Authority: AFS 2023:15, Chapter 6 and Annex 6; amendment AFS 2025:7." },
  },

  {
    category: "afs-asbest-kvarts", slug: "infor-besoket",
    label: { sv: "Pris och förberedelser", en: "Prepare your appointment" },
    title: { sv: "Asbest, kvarts och fibrer – förbered din kontroll", en: "Asbestos, silica and fibres – preparing your medical examination" },
    description: { sv: "Det här behöver ni förbereda när vi planerar kontrollen tillsammans.", en: "What you need to prepare when we plan the examination together." },
    sections: [
      { id: "bokning", title: { sv: "Börja med en förfrågan", en: "Send an enquiry" }, paragraphs: { sv: ["Skicka formuläret så samordnar vi underlag, undersökningar och besök med er."], en: ["Send an enquiry so we can coordinate records, examinations and appointments."] }, link: { href: "/lakarintyg/afs-asbest-kvarts-och-syntetiska-oorganiska-fibrer#planera-kontroll", label: { sv: "Skicka förfrågan", en: "Send an enquiry" } } },
      { id: "pris", title: { sv: "Pris och beställning", en: "Price and what is included" }, paragraphs: { sv: ["Pris: 4 000 kr. Vi bekräftar omfattning och kostnad innan bokningen."], en: ["Price: 4 000 SEK. We confirm the scope and cost before booking."] } },
      { id: "checklista", title: { sv: "Förbered dessa uppgifter", en: "Have these ready" }, paragraphs: { sv: ["Ta fram underlagen i god tid."], en: ["Prepare the documents in good time."] }, items: { sv: [...commonWorkEnvPrep.sv, "Uppgifter om dammtyp, exponeringsmätningar och hur länge arbetet pågår.", "Tidigare tjänstbarhetsintyg och relevanta lungundersökningar om sådana finns."], en: [...commonWorkEnvPrep.en, "Dust type, exposure measurements and duration of the work.", "Previous fitness certificates and relevant lung examination results."] } },
      { id: "efter", title: { sv: "Efter kontrollen", en: "After the assessment" }, paragraphs: { sv: ["Tjänstbarhetsintyg utfärdas efter medicinsk bedömning. Du får besked om eventuella kompletteringar och fortsatt uppföljning."], en: ["A fitness certificate is issued after the medical assessment. You will be advised of any further steps and follow-up."] } },
    ],
    source: { sv: "Intygsläkarens besöksupplägg och Arbetsmiljöverkets AFS 2023:15.", en: "Intygsläkaren visit information and Swedish Work Environment Authority AFS 2023:15." },
  },

  // ── AFS BLY ───────────────────────────────────────────────────────────────
  {
    category: "afs-bly", slug: "regler-och-kontroll",
    label: { sv: "Regler och intervall", en: "Requirements and examination" },
    title: { sv: "Blyarbete – krav och medicinsk kontroll", en: "Lead exposure – medical examination requirements" },
    description: { sv: "När kontrollen behövs, vad som undersöks och hur uppföljningen planeras.", en: "When the examination is required, what is examined and how follow-up is planned." },
    sections: [
      { id: "nar", title: { sv: "När behövs kontrollen?", en: "When is the examination required?" }, paragraphs: { sv: ["Blyarbete som omfattas av reglerna kräver både medicinsk kontroll med bedömning för tjänstbarhetsintyg och biologisk exponeringskontroll genom blodprov. Exponeringen behöver bedömas även om skyddsutrustning används."], en: ["Lead work covered by the regulations requires both a medical examination for a fitness certificate and biological exposure monitoring with blood tests. Exposure must be assessed even when protective equipment is used."] } },
      { id: "intervall", title: { sv: "När ska den göras och upprepas?", en: "Timing and repeat examinations" }, paragraphs: { sv: ["Provtagningen och läkarundersökningen följer olika intervall. Vi planerar uppföljningen utifrån exponering, tidigare resultat och de aktuella reglerna. Sänkta gränsvärden för bly i blod gäller sedan 9 april 2026."], en: ["Blood tests and medical examinations follow different intervals. Follow-up is planned according to exposure, previous results and current rules. Lower blood lead limits apply from 9 April 2026."] } },
      { id: "undersokning", title: { sv: "Vad görs vid kontrollen?", en: "What is examined?" }, paragraphs: { sv: ["Beskriv arbetsmomenten och om det gäller en första kontroll eller uppföljning. Vi återkommer om vilka tidigare provsvar och uppgifter om exponeringen som behövs för planeringen."], en: ["Describe the tasks and whether this is an initial examination or follow-up. We advise which exposure information and previous blood lead results are needed."] }, items: { sv: ["Genomgång av blyexponering och tidigare provtagning", "Medicinsk undersökning och bedömning för tjänstbarhetsintyg", "Planering av blodprov och fortsatt uppföljning"], en: ["Review of lead exposure and previous testing", "Medical examination and assessment for a fitness certificate", "Planning of blood testing and follow-up"] } },
      { id: "intyg", title: { sv: "Kontroll och tjänstbarhetsintyg", en: "Examination and fitness certificate" }, paragraphs: { sv: ["Ett tjänstbarhetsintyg ersätter inte de återkommande blodproverna. Båda delarna behöver planeras för arbete som omfattas av reglerna."], en: ["A fitness certificate does not replace recurring blood tests. Both parts need to be planned for work covered by the regulations."] } },
      { id: "arbetsgivare", title: { sv: "Arbetsgivarens underlag behövs", en: "Information from the employer" }, paragraphs: { sv: ["Arbetsgivarens riskbedömning visar vilka arbetsmoment och exponeringar som behöver bedömas."], en: ["The employer's risk assessment shows which tasks and exposures need to be assessed."] }, link: { href: "/guider/afs-bly/infor-besoket", label: { sv: "Pris och förberedelser", en: "Price and preparation" } } },
    ],
    source: { sv: "Arbetsmiljöverket, AFS 2023:15, 7 kap. 1–10 §§ och bilaga 7; ändring AFS 2025:3.", en: "Swedish Work Environment Authority: AFS 2023:15, Chapter 7, sections 1–10 and Annex 7; amendment AFS 2025:3." },
  },

  {
    category: "afs-bly", slug: "infor-besoket",
    label: { sv: "Pris och förberedelser", en: "Prepare your appointment" },
    title: { sv: "Blyarbete – förbered din kontroll", en: "Lead exposure – preparing your medical examination" },
    description: { sv: "Det här behöver ni förbereda när vi planerar kontrollen tillsammans.", en: "What you need to prepare when we plan the examination together." },
    sections: [
      { id: "bokning", title: { sv: "Börja med en förfrågan", en: "Send an enquiry" }, paragraphs: { sv: ["Skicka en förfrågan så planerar vi kontrollen och eventuell fortsatt provtagning med er."], en: ["Send an enquiry so we can plan the examination and any continued monitoring."] }, link: { href: "/lakarintyg/afs-bly#planera-kontroll", label: { sv: "Skicka förfrågan", en: "Send an enquiry" } } },
      { id: "pris", title: { sv: "Pris och beställning", en: "Price and what is included" }, paragraphs: { sv: ["Pris: 4 000 kr. Vi bekräftar omfattning och kostnad innan bokningen."], en: ["Price: 4 000 SEK. We confirm the scope and cost before booking."] } },
      { id: "checklista", title: { sv: "Förbered dessa uppgifter", en: "Have these ready" }, paragraphs: { sv: ["Ta fram underlagen i god tid."], en: ["Prepare the documents in good time."] }, items: { sv: [...commonWorkEnvPrep.sv, "Arbetsmoment och uppgifter om möjlig blyexponering.", "Tidigare blyhalter i blod och datum för senaste medicinska kontrollen."], en: [...commonWorkEnvPrep.en, "Tasks and details of possible lead exposure.", "Previous blood lead results and the date of the last medical examination."] } },
      { id: "efter", title: { sv: "Efter kontrollen", en: "After the assessment" }, paragraphs: { sv: ["Tjänstbarhetsintyg utfärdas och blodprovtagning planeras. Du får besked om eventuella kompletteringar och fortsatt uppföljning."], en: ["A fitness certificate is issued and blood testing is planned. You will be advised of any further steps."] } },
    ],
    source: { sv: "Intygsläkarens besöksupplägg och Arbetsmiljöverkets AFS 2023:15.", en: "Intygsläkaren visit information and Swedish Work Environment Authority AFS 2023:15." },
  },

  // ── AFS KLÄTTRING ─────────────────────────────────────────────────────────
  {
    category: "afs-klattring", slug: "regler-och-kontroll",
    label: { sv: "Regler och intervall", en: "Requirements and examination" },
    title: { sv: "Klättring och höjdarbete – krav och medicinsk kontroll", en: "Climbing at height – medical examination requirements" },
    description: { sv: "När kontrollen behövs, vad som undersöks och hur uppföljningen planeras.", en: "When the examination is required, what is examined and how follow-up is planned." },
    sections: [
      { id: "nar", title: { sv: "När behövs kontrollen?", en: "When is the examination required?" }, paragraphs: { sv: ["Det särskilda kravet gäller klättring med mer än 13 meters nivåskillnad där det finns risk för fritt eller okontrollerat fall, eller där undsättning är försvårad. Allt arbete på höjd omfattas alltså inte automatiskt av samma intygskrav."], en: ["The specific requirement applies to climbing with a height difference of more than 13 metres where there is a risk of free or uncontrolled falling, or rescue is difficult. Not all work at height automatically has the same certificate requirement."] } },
      { id: "intervall", title: { sv: "När ska den göras och upprepas?", en: "Timing and repeat examinations" }, paragraphs: { sv: ["Kontrollen ska göras före arbetets början och därefter med högst tolv månaders mellanrum när arbetet omfattas av reglerna. Planera i god tid före giltighetstidens slut."], en: ["Before the work starts and then at intervals of no more than twelve months for work covered by the regulations. Plan the next visit before the certificate expires."] } },
      { id: "undersokning", title: { sv: "Vad görs vid kontrollen?", en: "What is examined?" }, paragraphs: { sv: ["Beskriv klättringens höjd, arbetsmomenten och möjligheten till räddning. Vi stämmer av om arbetet omfattas av kraven och hur nödvändiga undersökningar ska ordnas."], en: ["Describe the climbing height, tasks and rescue arrangements. We review whether the work is covered by the requirements and how the necessary examinations should be arranged."] }, items: { sv: ["Genomgång av klättring, arbetsbelastning och hälsa", "Läkarundersökning och bedömning av fysisk arbetsförmåga", "Planering av de funktions- och hjärtundersökningar som krävs"], en: ["Review of climbing tasks, physical load and health", "Medical examination and assessment of physical work capacity", "Planning of the required functional and cardiac examinations"] } },
      { id: "intyg", title: { sv: "Kontroll och tjänstbarhetsintyg", en: "Examination and fitness certificate" }, paragraphs: { sv: ["Arbete som omfattas av reglerna kräver tjänstbarhetsintyg. Bedömningen gäller den fysiska förmåga och hälsa som behövs för det aktuella arbetet."], en: ["Work covered by the rules requires a certificate of fitness for work. The assessment concerns the physical capacity and health needed for the work."] } },
      { id: "arbetsgivare", title: { sv: "Arbetsgivarens underlag behövs", en: "Information from the employer" }, paragraphs: { sv: ["Arbetsgivarens riskbedömning visar vilka arbetsmoment och exponeringar som behöver bedömas."], en: ["The employer's risk assessment shows which tasks and exposures need to be assessed."] }, link: { href: "/guider/afs-klattring/infor-besoket", label: { sv: "Pris och förberedelser", en: "Price and preparation" } } },
    ],
    source: { sv: "Arbetsmiljöverket, AFS 2023:15, 8 kap. 1–3 §§ och bilaga 10.", en: "Swedish Work Environment Authority: AFS 2023:15, Chapter 8, sections 1–3 and Annex 10." },
  },

  {
    category: "afs-klattring", slug: "infor-besoket",
    label: { sv: "Pris och förberedelser", en: "Prepare your appointment" },
    title: { sv: "Klättring och höjdarbete – förbered din kontroll", en: "Climbing at height – preparing your medical examination" },
    description: { sv: "Det här behöver ni förbereda när vi planerar kontrollen tillsammans.", en: "What you need to prepare when we plan the examination together." },
    sections: [
      { id: "bokning", title: { sv: "Börja med en förfrågan", en: "Send an enquiry" }, paragraphs: { sv: ["Skicka en förfrågan så går vi igenom arbetskraven och planerar undersökningen."], en: ["Send an enquiry so we can review the work requirements and plan the examination."] }, link: { href: "/lakarintyg/afs-klattring-med-stor-nivskillnad#planera-kontroll", label: { sv: "Skicka förfrågan", en: "Send an enquiry" } } },
      { id: "pris", title: { sv: "Pris och beställning", en: "Price and what is included" }, paragraphs: { sv: ["Pris: Från 4 000 kr. Vi bekräftar omfattning och kostnad innan bokningen."], en: ["Price: from 4 000 SEK. We confirm the scope and cost before booking."] } },
      { id: "checklista", title: { sv: "Förbered dessa uppgifter", en: "Have these ready" }, paragraphs: { sv: ["Ta fram underlagen i god tid."], en: ["Prepare the documents in good time."] }, items: { sv: [...commonWorkEnvPrep.sv, "Beskrivning av höjd, klättring och räddningsförhållanden.", "Tidigare tjänstbarhetsintyg och relevanta undersökningsresultat."], en: [...commonWorkEnvPrep.en, "A description of the height, climbing and rescue conditions.", "Previous fitness certificates and relevant examination results."] } },
      { id: "efter", title: { sv: "Efter kontrollen", en: "After the assessment" }, paragraphs: { sv: ["Tjänstbarhetsintyg utfärdas och eventuella funktionstester planeras. Du får besked om eventuella kompletteringar och fortsatt uppföljning."], en: ["A fitness certificate is issued and any functional tests are planned. You will be advised of any further steps."] } },
    ],
    source: { sv: "Intygsläkarens besöksupplägg och Arbetsmiljöverkets AFS 2023:15.", en: "Intygsläkaren visit information and Swedish Work Environment Authority AFS 2023:15." },
  },

  // ── ALKOLÅS ────────────────────────────────────────────────────────────────
  {
    category: "alkolas", slug: "guide",
    label: { sv: "Guide till alkolås", en: "Alcohol interlock guide" },
    title: { sv: "Guide: Läkarintyg för alkolås", en: "Guide: Medical certificate for an alcohol interlock" },
    description: {
      sv: "Från ansökan till färdigt intyg – prover, läkarbedömning och vad villkorstiden innebär.",
      en: "From application to finished certificate – tests, medical assessment and what the conditional period means.",
    },
    sections: [
      {
        id: "oversikt",
        title: { sv: "Läkarbedömning och prover för ditt alkolåsintyg", en: "Medical assessment and tests for your certificate" },
        paragraphs: {
          sv: [
            "Ansökan till Transportstyrelsen om alkolås består av en viljeförklaring och ett läkarintyg. Vi hjälper dig med den medicinska delen och planerar de prover och undersökningar som behövs.",
            "Provtagningen är en del av den medicinska bedömningen inför din ansökan. Vi ordnar remiss och går igenom provsvaren innan intyget färdigställs. Du lämnar prover hos oss i Göteborg eller på en anvisad provtagningsplats nära dig.",
          ],
          en: [
            "The application to the Swedish Transport Agency for an alcohol interlock consists of a statement of intent and a medical certificate. We help you with the medical part and plan the tests and examinations needed.",
            "Testing is part of the medical assessment for your application. We arrange the referral and review the results before the certificate is completed. You provide samples at our clinic in Gothenburg or at an assigned testing location near you.",
          ],
        },
      },
      {
        id: "pris",
        title: { sv: "Pris och vad som ingår", en: "Price and what is included" },
        paragraphs: {
          sv: [
            "Läkarintyg inklusive prover kostar 2 995 kr. Vi går igenom vilka prover som ingår utifrån ditt underlag. Om fler prover behövs får du information om kostnaden innan de tas.",
          ],
          en: [
            "The medical certificate including tests costs 2 995 SEK. We review which tests are included based on your documentation. If more tests are needed, you will be informed of the cost before they are carried out.",
          ],
        },
        table: {
          columns: { sv: ["Tjänst", "Pris"], en: ["Service", "Price"] },
          rows: {
            sv: [
              ["Alkolåsintyg inklusive prover", "2 995 kr"],
              ["Ytterligare provtagning (per tillfälle)", "1 500 kr"],
              ["Verifieringsprover", "1 500 kr"],
            ],
            en: [
              ["Alcohol interlock certificate including tests", "2 995 SEK"],
              ["Additional sampling (per occasion)", "1 500 SEK"],
              ["Confirmatory tests", "1 500 SEK"],
            ],
          },
        },
      },
      {
        id: "steg",
        title: { sv: "Så går det till", en: "How it works" },
        paragraphs: {
          sv: ["Läkarbedömningen och provtagningen följer ett tydligt upplägg. Här är stegen:"],
          en: ["The medical assessment and testing follow a clear process:"],
        },
        items: {
          sv: [
            "Boka läkarbesök och välj alkolås i bokningen.",
            "Ha Transportstyrelsens handlingar och aktuell läkemedelslista redo. Medicinska uppgifter lämnas via journalsystemet.",
            "Läkaren går igenom din situation, vilka undersökningar som behövs och hur provtagningen ska genomföras.",
            "Du lämnar prover hos oss i Göteborg eller på en lämplig provtagningsplats nära dig.",
            "När nödvändiga provsvar och underlag finns gör läkaren sin bedömning och färdigställer intyget.",
          ],
          en: [
            "Book a medical appointment and select alcohol interlock.",
            "Have the Transport Agency's documents and current medication list ready. Medical information is submitted via the medical record system.",
            "The doctor reviews your situation, identifies needed examinations and plans the testing.",
            "You provide samples at our Gothenburg clinic or at a suitable location near you.",
            "When the necessary results and documentation are available, the doctor makes the assessment and completes the certificate.",
          ],
        },
      },
      {
        id: "provtagning",
        title: { sv: "Digital läkarkontakt, fysisk provtagning", en: "Digital contact, in-person testing" },
        paragraphs: {
          sv: [
            "Du kan få hjälp även om du inte bor i Göteborg. Läkarkontakten kan ske digitalt när det är medicinskt lämpligt. Provtagningen görs fysiskt på anvisad plats.",
            "Vi använder Provio, Synlab och Unilabs för att ordna provtagning där det är möjligt. Plats och tillgängliga analyser bekräftas när vi planerar ditt ärende.",
          ],
          en: [
            "You can get help even if you do not live in Gothenburg. The medical contact can take place digitally when medically appropriate. Testing takes place in person at an assigned location.",
            "We use Provio, Synlab and Unilabs to arrange testing where possible. Location and available analyses are confirmed when we plan your case.",
          ],
        },
      },
      {
        id: "vanliga-fragor",
        title: { sv: "Vanliga frågor om alkolåsintyg", en: "Frequently asked questions" },
        paragraphs: {
          sv: ["Här är svar på de vanligaste frågorna:"],
          en: ["Answers to the most common questions:"],
        },
        items: {
          sv: [
            "Kan jag få hjälp på distans? – Läkarkontakten kan ske digitalt när det är medicinskt lämpligt. Provtagningen görs fysiskt.",
            "Vad kostar läkarintyg för alkolås? – Priset är 2 995 kr inklusive prover. Ytterligare provtagning kostar 1 500 kr per tillfälle.",
            "Behöver jag prover även när alkolåset är installerat? – Det beror på villkorstiden och ditt beslut. Vid två års villkorstid krävs uppföljning med läkarintyg och provresultat.",
            "Är läkarintyget samma sak som ett godkännande av alkolås? – Nej. Läkarintyget är ett medicinskt underlag. Transportstyrelsen prövar ansökan och beslutar om körkort med villkor om alkolås.",
          ],
          en: [
            "Can I get help remotely? – The medical contact can take place digitally when medically appropriate. Testing takes place in person.",
            "What does the certificate cost? – The price is 2 995 SEK including tests. Additional sampling costs 1 500 SEK per occasion.",
            "Do I need tests once the interlock is installed? – It depends on your conditional period and decision. A two-year conditional period requires follow-up with a medical certificate and test results.",
            "Is the medical certificate the same as approval of an alcohol interlock? – No. The certificate is medical evidence. The Transport Agency decides on a driving licence with an alcohol interlock condition.",
          ],
        },
      },
    ],
    source: {
      sv: "Intygsläkarens information om alkolåsintyg och provtagning.",
      en: "Intygsläkaren information on alcohol interlock certificates and testing.",
    },
    relatedCert: "alkolas",
    bookingHref: "/boka-tid?intyg=alkolas",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────

export function getGuideArticle(category: string, slug: string): GuideArticle | null {
  return guideArticles.find(g => g.category === category && g.slug === slug) ?? null;
}

export function guidesInCategory(category: string, excludeSlug?: string): GuideArticle[] {
  return guideArticles.filter(g => g.category === category && g.slug !== excludeSlug);
}

export { guideHref } from "@/data/guideRoutes";

/** Rewrite Swedish-canonical in-article hrefs for the active language. */
export function localizeGuideLink(href: string, lang: "sv" | "en"): string {
  if (lang === "sv" || href.startsWith("http") || href.startsWith("#")) return href;

  const [pathPart, hash = ""] = href.split("#");
  const [pathname, query = ""] = pathPart.split("?");
  const suffix = (query ? `?${query}` : "") + (hash ? `#${hash}` : "");

  if (pathname.startsWith("/guider/")) {
    return `/en/guides/${pathname.slice("/guider/".length)}${suffix}`;
  }
  if (pathname === "/boka-tid") {
    return `/en/book-appointment${suffix}`;
  }
  if (pathname === "/kontakt") {
    return `/en/contact${suffix}`;
  }
  if (pathname.startsWith("/lakarintyg/")) {
    const publicSlug = pathname.slice("/lakarintyg/".length);
    const internal = findCertificateBySlug(publicSlug, "sv");
    if (internal) return `${certificatePathFor(internal, "en")}${suffix}`;
  }
  return href;
}
